import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import { createClient } from '@supabase/supabase-js';
import { permissions } from './config/permissions';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || 'https://nsfnhzqrhcippaheyiev.supabase.co',
  process.env.REACT_APP_SUPABASE_ANON_KEY || 'REEMPLAZAR_CON_TU_ANON_KEY'
);

const API_URL = '';

// Helper que agrega el token de autenticación a todos los requests al dashboard
function authFetch(url, options = {}) {
  const token = localStorage.getItem('wc_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  });
}

const VENTAS_STORAGE_KEY = 'weconnect_ventas_local_data_v2';
const ATENCION_STORAGE_KEY = 'weconnect_atencion_local_data_v1';
const SOPORTE_STORAGE_KEY = 'weconnect_soporte_local_data_v1';

const ETAPAS_VENTAS = [
  'Lead nuevo',
  'Contactado',
  'Calificado',
  'Cobertura validada',
  'Propuesta enviada',
  'Seguimiento',
  'Aceptó oferta',
  'Venta confirmada',
  'Venta perdida',
  'Sin cobertura expansión',
];

const ESTADOS_ATENCION = [
  'Pendiente de revisión',
  'Esperando cliente',
  'Esperando pago',
  'Factura enviada',
  'Resuelto',
];

const ESTADOS_SOPORTE = [
  'Pendiente de revisión',
  'Diagnóstico en curso',
  'Esperando cliente',
  'Derivado a visita técnica',
  'Resuelto',
];

const ESTADOS_ACTIVOS = ['abierta', 'en_curso', 'activa', 'pendiente', 'escalada'];

const demoUsers = [
  {
    id: 'admin-general',
    nombre: 'Admin General',
    nivel: 'admin',
    areas: ['ventas', 'atencion', 'soporte'],
  },
  {
    id: 'agente-comercial',
    nombre: 'Agente Ventas + Atención',
    nivel: 'agente',
    areas: ['ventas', 'atencion'],
  },
  {
    id: 'agente-soporte',
    nombre: 'Agente Soporte',
    nivel: 'agente',
    areas: ['soporte'],
  },
  {
    id: 'nocturno',
    nombre: 'Nocturno Full',
    nivel: 'agente',
    areas: ['ventas', 'atencion', 'soporte'],
  },

];

const DEFAULT_SALES_SUMMARY = {
  nombre_apellido: '',
  cuil: '',
  email: '',
  direccion: '',
  entre_calle_1: '',
  entre_calle_2: '',
  localidad: '',
  telefono: '',
  plan: '',
  precio: '',
  medio_pago: '',
  latitud: '',
  longitud: '',
  mapa_url: '',
  observaciones: '',
};

const LOCALIDADES_EXPANSION = ['Capitán Sarmiento', 'Florencio Varela', 'Almirante Brown'];

function normalizarTexto(texto = '') {
  return String(texto || '').trim();
}

function normalizarTelefonoVisual(telefono = '') {
  return String(telefono || '').replace(/\D/g, '');
}

function formatDateTimeLocal(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
}

function formatearEntreCalles(calle1 = '', calle2 = '') {
  const a = normalizarTexto(calle1);
  const b = normalizarTexto(calle2);
  if (a && b) return `${a} y ${b}`;
  if (a) return a;
  if (b) return b;
  return '';
}

function extraerEmailDesdeMensajes(listaMensajes = []) {
  for (const msg of listaMensajes) {
    const contenido = String(msg?.contenido || '');
    const match = contenido.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    if (match) return match[0];
  }
  return '';
}
function esDocumentoValidoFront(valor = '') {
  const digits = String(valor || '').replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 11;
}
function normalizarComparableFront(texto = '') {
  return String(texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function extraerDocumentoDesdeMensajes(listaMensajes = []) {
  let candidato = '';
  for (let i = listaMensajes.length - 1; i >= 0; i -= 1) {
    const msg = listaMensajes[i];
    if (msg?.rol !== 'cliente') continue;
    const contenido = String(msg?.contenido || '').replace(/\D/g, '');
    if (!esDocumentoValidoFront(contenido)) continue;

    const previo = normalizarComparableFront(listaMensajes[i - 1]?.contenido || '');
    if (previo.includes('cuil') || previo.includes('dni')) return contenido;
    if (!candidato) candidato = contenido;
  }
  return candidato;
}

const LOCALIDADES_REFERENCIA_FRONT = [
  'almirante brown',
  'burzaco',
  'adrogue',
  'adrogué',
  'longchamps',
  'longchamp',
  'glew',
  'claypole',
  'jose marmol',
  'josé mármol',
  'rafael calzada',
  'san jose',
  'san josé',
  'florencio varela',
  'bosques',
  'ingeniero allan',
  'villa vatteone',
  'villa brown',
  'capitan sarmiento',
  'capitán sarmiento',
  'pablo podesta',
  'pablo podestá'
];

const PALABRAS_NO_DIRECCION = [
  'gamer',
  'gaming',
  'streaming',
  'trabajo remoto',
  'uso general',
  'mercadopago',
  'mercado pago',
  'tarjeta',
  'transferencia',
  'pago facil',
  'pago fácil',
  'quiero',
  'me interesa',
  'megas',
  'mb',
  'mbps',
  '300',
  '600',
  '100'
];

function capitalizarCadaPalabraFront(texto = '') {
  return String(texto || '')
    .toLowerCase()
    .replace(/\w/g, (c) => c.toUpperCase())
    .replace(/Y/g, 'y');
}

function esLocalidadValidaFront(localidad = '', direccion = '') {
  if (!localidad || String(localidad).trim().length < 3) return false;
  const texto = String(localidad).trim().toLowerCase();
  // Rechazar valores que claramente no son localidades
  const basura = ['si', 'no', 'ok', 'dale', 'bien', 'gracias', 'hola', 'buenas', 'perfecto'];
  if (basura.includes(texto)) return false;
  // Aceptar si es una localidad conocida de la zona de cobertura
  const esConocida = LOCALIDADES_REFERENCIA_FRONT.some((loc) => texto.includes(loc) || loc.includes(texto));
  if (esConocida) return true;
  // Aceptar si tiene al menos 4 chars y no es un número solo
  if (texto.length >= 4 && !/^\d+$/.test(texto)) return true;
  return false;
}

function esProbableDireccion(contenido = '') {
  const texto = String(contenido || '').trim();
  if (!texto) return false;
  if (texto.includes('@')) return false;
  if (/^\d{10,13}$/.test(texto.replace(/\D/g, ''))) return false;
  if (!/\d/.test(texto) || !/[a-záéíóúñ]/i.test(texto)) return false;

  const normalizado = texto.toLowerCase();
  if (PALABRAS_NO_DIRECCION.some((palabra) => normalizado.includes(palabra))) {
    const tieneCalleConNumero = /[a-záéíóúñ]{3,}.*\d{1,5}/i.test(texto);
    const mencionaLocalidad = LOCALIDADES_REFERENCIA_FRONT.some((loc) => normalizado.includes(loc));
    if (!tieneCalleConNumero || !mencionaLocalidad) return false;
  }

  return /[a-záéíóúñ]{3,}.*\d{1,5}/i.test(texto);
}

function mensajePrevioPideDireccion(textoPrevio = '') {
  const previo = String(textoPrevio || '').toLowerCase();
  return (
    (previo.includes('dirección') || previo.includes('direccion')) &&
    (previo.includes('localidad') || previo.includes('cobertura'))
  );
}

function extraerDireccionDesdeMensajes(listaMensajes = []) {
  let candidataFinal = '';

  for (let i = 0; i < listaMensajes.length; i += 1) {
    const actual = listaMensajes[i];
    if (actual?.rol !== 'cliente') continue;
    const contenido = String(actual?.contenido || '').trim();
    if (!esProbableDireccion(contenido)) continue;

    const previo = String(listaMensajes[i - 1]?.contenido || '');
    if (mensajePrevioPideDireccion(previo)) {
      return contenido;
    }

    candidataFinal = contenido;
  }

  return candidataFinal;
}

function inferirLocalidadDesdeDireccion(direccion = '') {
  const texto = String(direccion || '').trim().toLowerCase();
  if (!texto) return '';

  // Buscar todas las localidades que aparecen en el texto
  // y quedarse con la que aparece más al final (las calles van al inicio)
  let mejorLocalidad = '';
  let mejorPos = -1;

  for (const localidad of LOCALIDADES_REFERENCIA_FRONT) {
    const pos = texto.lastIndexOf(localidad);
    if (pos !== -1 && pos > mejorPos) {
      mejorPos = pos;
      mejorLocalidad = localidad;
    }
  }

  return mejorLocalidad ? capitalizarCadaPalabraFront(mejorLocalidad) : '';
}

function limpiarDireccionSinLocalidad(direccion = '', localidad = '') {
  let texto = String(direccion || '').trim();
  const candidatas = [localidad, ...LOCALIDADES_REFERENCIA_FRONT.map(capitalizarCadaPalabraFront)].filter(Boolean);
  for (const candidata of candidatas) {
   const escapada = String(candidata).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`([,\s]+)${escapada}$`, 'i');
    texto = texto.replace(regex, '').trim().replace(/,$/, '').trim();
  }
  return texto;
}

async function geocodificarResumenVentaFrontend({ direccion = '', entre_calle_1 = '', entre_calle_2 = '', localidad = '' } = {}) {
  try {
    const response = await authFetch(`${API_URL}/api/dashboard/ventas/geocodificar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ direccion, entre_calle_1, entre_calle_2, localidad }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) return null;
    // Devolver el objeto completo con coordenadas + dirección/localidad canónicas
    return {
      coordenadas: data?.coordenadas || null,
      direccion_canonica: data?.direccion_canonica || '',
      localidad_canonica: data?.localidad_canonica || '',
      display_name: data?.display_name || '',
    };
  } catch (error) {
    console.error('Error geocodificando resumen venta:', error);
    return null;
  }
}

function extraerNombreCompletoDesdeMensajes(listaMensajes = []) {
  // Frases que NO son nombres: confirmaciones, saludos, respuestas genéricas
  const FRASES_NO_NOMBRE = [
    'todo correcto', 'todo bien', 'todo perfecto', 'esta todo correcto',
    'esta bien', 'esta perfecto', 'esta todo bien', 'correcto todo',
    'todo ok', 'todo ok gracias', 'gracias', 'muchas gracias', 'ok gracias',
    'si gracias', 'no gracias', 'listo', 'dale', 'bueno', 'buenas',
    'hola', 'buen dia', 'buenos dias', 'buenas tardes', 'buenas noches',
    'correcto', 'confirmado', 'confirmo', 'perfecto', 'esta perfecto',
    'muy bien', 'esta muy bien', 'asi es', 'exacto', 'nada mas',
    'nada gracias', 'esta bien gracias', 'ok', 'okay',
  ];
  
  for (let i = 0; i < listaMensajes.length; i += 1) {
    const actual = listaMensajes[i];
    const contenido = String(actual?.contenido || '').trim();
    if (actual?.rol !== 'cliente') continue;
    if (!contenido || contenido.includes('@') || /\d/.test(contenido)) continue;
    const palabras = contenido.split(/\s+/);
    if (palabras.length < 2) continue;
    // Rechazar intersecciones de calles: "Alsina y Amancio Alcorta"
    const hayY = palabras.some((p, idx) => idx > 0 && idx < palabras.length - 1 && p.toLowerCase() === 'y');
    if (hayY) continue;
    // Rechazar si contiene palabras típicas de direcciones
    const textoBajo = contenido.toLowerCase();
    const esDireccion = PALABRAS_NO_DIRECCION.some((p) => textoBajo.includes(p)) ||
      /\b(calle|av\.?|avenida|pasaje|blvd|entre|esquina)\b/i.test(contenido);
    if (esDireccion) continue;
    // Rechazar frases que son confirmaciones, no nombres
    const textoNorm = textoBajo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    if (FRASES_NO_NOMBRE.some((f) => textoNorm === f || textoNorm.startsWith(f + ' '))) continue;
    // Requerir que el previo sea pregunta explícita de nombre
    const previo = String(listaMensajes[i - 1]?.contenido || '').toLowerCase();
    const preguntoNombre = (
      previo.includes('nombre y apellido') ||
      previo.includes('nombre completo') ||
      previo.includes('tu nombre') ||
      (previo.includes('titular') && previo.includes('?'))
    );
    if (preguntoNombre) {
      return contenido
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
  }
  return '';
}

function extraerPrecioDesdeMensajes(listaMensajes = []) {
  for (let i = listaMensajes.length - 1; i >= 0; i -= 1) {
    const contenido = String(listaMensajes[i]?.contenido || '');
    const match = contenido.match(/\$\s*([\d\.]+)/);
    if (match) return match[1].replace(/\./g, '');
  }
  return '';
}

function extraerMedioPagoDesdeMensajes(listaMensajes = []) {
  for (let i = listaMensajes.length - 1; i >= 0; i -= 1) {
    const contenido = String(listaMensajes[i]?.contenido || '').toLowerCase();
    if (contenido.includes('mercado')) return 'Mercado Pago';
    if (contenido.includes('transfer')) return 'Transferencia';
    if (contenido.includes('pago fácil') || contenido.includes('pagofacil')) return 'Pago Fácil';
    if (contenido.includes('tarjeta')) return 'Tarjeta';
  }
  return '';
}

function crearFormularioVentaInicial({
  conversacion,
  mensajes = [],
  datosCliente = null,
  ventaActual = null,
  resumenGuardado = {},
  agenteNombre = '',
}) {
  const emailDetectado = extraerEmailDesdeMensajes(mensajes);
  const cuilDetectado = extraerDocumentoDesdeMensajes(mensajes);
  const direccionDetectada = extraerDireccionDesdeMensajes(mensajes);
  const direccionBase = esProbableDireccion(direccionDetectada)
    ? direccionDetectada
    : (esProbableDireccion(resumenGuardado.direccion) ? resumenGuardado.direccion : '');
  const localidadDetectadaChat = inferirLocalidadDesdeDireccion(direccionBase);
  const localidadGuardadaValida = esLocalidadValidaFront(resumenGuardado.localidad, direccionBase)
    ? capitalizarCadaPalabraFront(resumenGuardado.localidad)
    : '';
  const localidadDetectada = localidadDetectadaChat || localidadGuardadaValida;
  const direccion = limpiarDireccionSinLocalidad(direccionBase, localidadDetectada);
  const email = emailDetectado || resumenGuardado.email || '';
  const cuil = esDocumentoValidoFront(cuilDetectado)
    ? cuilDetectado
    : (esDocumentoValidoFront(resumenGuardado.cuil) ? resumenGuardado.cuil : '');
  const telefono = normalizarTelefonoVisual(resumenGuardado.telefono || conversacion?.telefono || '');
  const plan = resumenGuardado.plan || ventaActual?.planSugerido || datosCliente?.plan || '';
  const precio = resumenGuardado.precio || extraerPrecioDesdeMensajes(mensajes);
  const medioPago = resumenGuardado.medio_pago || extraerMedioPagoDesdeMensajes(mensajes);
  const nombreDetectado =
    extraerNombreCompletoDesdeMensajes(mensajes) ||
    resumenGuardado.nombre_apellido ||
    conversacion?.nombre_cliente ||
    '';

  return {
    ...DEFAULT_SALES_SUMMARY,
    ...resumenGuardado,
    nombre_apellido: nombreDetectado,
    cuil,
    email,
    direccion,
    entre_calle_1: resumenGuardado.entre_calle_1 || '',
    entre_calle_2: resumenGuardado.entre_calle_2 || '',
    localidad: localidadDetectada,
    telefono,
    plan,
    precio,
    medio_pago: medioPago,
    latitud: resumenGuardado.latitud || '',
    longitud: resumenGuardado.longitud || '',
    mapa_url: resumenGuardado.mapa_url || '',
    observaciones: resumenGuardado.observaciones || '',
    agente_nombre: agenteNombre || '',
  };
}

// ── Pantalla de Login ──
function Login({ onLogin }) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [cargando, setCargando] = React.useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setCargando(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error al iniciar sesión'); return; }
      localStorage.setItem('wc_token', data.token);
      localStorage.setItem('wc_agente', JSON.stringify(data.agente));
      onLogin(data.agente, data.token);
    } catch { setError('Error de conexión. Verificá tu internet.'); }
    finally { setCargando(false); }
  };

  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)',display:'flex',alignItems:'center',justifyContent:'center',padding:'20px',fontFamily:'-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif'}}>
      <div style={{background:'rgba(255,255,255,0.05)',backdropFilter:'blur(20px)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'20px',padding:'40px',width:'100%',maxWidth:'400px',boxShadow:'0 25px 50px rgba(0,0,0,0.5)'}}>
        <div style={{display:'flex',alignItems:'center',gap:'14px',marginBottom:'36px'}}>
          <div style={{width:'48px',height:'48px',borderRadius:'14px',background:'linear-gradient(135deg,#7c3aed,#4f46e5)',color:'white',fontWeight:'800',fontSize:'18px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>WC</div>
          <div>
            <div style={{color:'white',fontWeight:'700',fontSize:'20px'}}>WeConnect</div>
            <div style={{color:'rgba(255,255,255,0.5)',fontSize:'12px'}}>Dashboard v2.0</div>
          </div>
        </div>
        <form onSubmit={handleLogin} style={{display:'flex',flexDirection:'column',gap:'16px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
            <label style={{color:'rgba(255,255,255,0.7)',fontSize:'13px',fontWeight:'500'}}>Email</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="tu@weconnect.ar" required autoFocus
              style={{background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'10px',padding:'12px 14px',color:'white',fontSize:'15px',outline:'none'}} />
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
            <label style={{color:'rgba(255,255,255,0.7)',fontSize:'13px',fontWeight:'500'}}>Contraseña</label>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required
              style={{background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'10px',padding:'12px 14px',color:'white',fontSize:'15px',outline:'none'}} />
          </div>
          {error && <div style={{background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'8px',padding:'10px 14px',color:'#fca5a5',fontSize:'13px'}}>{error}</div>}
          <button type="submit" disabled={cargando}
            style={{background:'linear-gradient(135deg,#7c3aed,#4f46e5)',border:'none',borderRadius:'10px',padding:'13px',color:'white',fontWeight:'600',fontSize:'15px',cursor:cargando?'not-allowed':'pointer',opacity:cargando?0.7:1}}>
            {cargando ? 'Ingresando...' : 'Ingresar'}
          </button>
          <div style={{color:'rgba(255,255,255,0.35)',fontSize:'12px',textAlign:'center'}}>¿Problemas para ingresar? Contactá a Fernando.</div>
        </form>
      </div>
    </div>
  );
}

function App() {
  // ── Autenticación ──
  const [autenticado, setAutenticado] = useState(() => {
    const token = localStorage.getItem('wc_token');
    const agente = localStorage.getItem('wc_agente');
    if (!token || !agente) return false;
    // Verificar que el token no expiró
    try {
      const data = token.split('.')[0];
      const payload = JSON.parse(atob(data));
      if (payload.exp < Date.now()) {
        localStorage.removeItem('wc_token');
        localStorage.removeItem('wc_agente');
        return false;
      }
      return true;
    } catch { return false; }
  });

  const handleLogin = (agente, token) => {
    setAutenticado(true);
    setAgenteLogueadoId(agente.id);
    localStorage.setItem('weconnect_agente_id', agente.id);
  };

  const handleLogout = () => {
    localStorage.removeItem('wc_token');
    localStorage.removeItem('wc_agente');
    localStorage.removeItem('weconnect_agente_id');
    setAutenticado(false);
    setAgenteLogueadoId(null);
  };

  const [selectedDemoUserId, setSelectedDemoUserId] = useState('admin-general');
  const [agenteLogueadoId, setAgenteLogueadoId] = useState(() => localStorage.getItem('weconnect_agente_id') || null);

  const currentUser =
    demoUsers.find((u) => u.id === selectedDemoUserId) || demoUsers[0];

  const currentPermissions = permissions[currentUser.nivel] || permissions.agente;
  const areasHabilitadas =
    currentUser.nivel === 'admin' || currentUser.nivel === 'supervisor'
      ? ['ventas', 'atencion', 'soporte']
      : currentUser.areas || [];

  const [conversaciones, setConversaciones] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [conversacionActiva, setConversacionActiva] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [vistaActiva, setVistaActiva] = useState('activas');
  const [datosCliente, setDatosCliente] = useState(null);
  const [enviandoMensaje, setEnviandoMensaje] = useState(false);
  const [cambiandoIA, setCambiandoIA] = useState(false);
  const [ventasLocalData, setVentasLocalData] = useState({});
  const [atencionLocalData, setAtencionLocalData] = useState({});
  const [soporteLocalData, setSoporteLocalData] = useState({});
  const [estadoOperativoBackend, setEstadoOperativoBackend] = useState(null);
  const [guardandoEstado, setGuardandoEstado] = useState(false);
  const [salesModal, setSalesModal] = useState({ open: false, type: null });
  const [salesModalForm, setSalesModalForm] = useState({});
  const [submittingSalesModal, setSubmittingSalesModal] = useState(false);

  // Soporte BCM
  const [diagnosticoBCM, setDiagnosticoBCM] = useState(null);
  const [ejecutandoAccionBCM, setEjecutandoAccionBCM] = useState(false);
  const [mensajeBCM, setMensajeBCM] = useState(null);
  const [reinicioConfirmado, setReinicioConfirmado] = useState(null);

  const conversacionActivaRef = useRef(null);

  const tieneAccesoArea = (categoria) => {
    if (!categoria) return true;
    if (currentUser.nivel === 'admin' || currentUser.nivel === 'supervisor') return true;
    return areasHabilitadas.includes(categoria);
  };

  useEffect(() => {
    conversacionActivaRef.current = conversacionActiva;
  }, [conversacionActiva]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(VENTAS_STORAGE_KEY);
      if (saved) setVentasLocalData(JSON.parse(saved));
    } catch (error) {
      console.error('Error leyendo ventas local storage:', error);
    }

    try {
      const saved = localStorage.getItem(ATENCION_STORAGE_KEY);
      if (saved) setAtencionLocalData(JSON.parse(saved));
    } catch (error) {
      console.error('Error leyendo atención local storage:', error);
    }

    try {
      const saved = localStorage.getItem(SOPORTE_STORAGE_KEY);
      if (saved) setSoporteLocalData(JSON.parse(saved));
    } catch (error) {
      console.error('Error leyendo soporte local storage:', error);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(VENTAS_STORAGE_KEY, JSON.stringify(ventasLocalData));
    } catch (error) {
      console.error('Error guardando ventas local storage:', error);
    }
  }, [ventasLocalData]);

  useEffect(() => {
    try {
      localStorage.setItem(ATENCION_STORAGE_KEY, JSON.stringify(atencionLocalData));
    } catch (error) {
      console.error('Error guardando atención local storage:', error);
    }
  }, [atencionLocalData]);

  useEffect(() => {
    try {
      localStorage.setItem(SOPORTE_STORAGE_KEY, JSON.stringify(soporteLocalData));
    } catch (error) {
      console.error('Error guardando soporte local storage:', error);
    }
  }, [soporteLocalData]);

  useEffect(() => {
    if (filtro !== 'todas' && !areasHabilitadas.includes(filtro)) {
      setFiltro('todas');
    }
  }, [filtro, areasHabilitadas]);

  useEffect(() => {
    if (conversacionActiva && !tieneAccesoArea(conversacionActiva.categoria || conversacionActiva.area)) {
      setConversacionActiva(null);
      setMensajes([]);
      setDatosCliente(null);
      setEstadoOperativoBackend(null);
    }
  }, [selectedDemoUserId]);

  const cargarConversaciones = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/dashboard/conversaciones`);
      const data = await response.json();
      setConversaciones(Array.isArray(data?.conversaciones) ? data.conversaciones : []);
    } catch (error) {
      console.error('Error cargando conversaciones:', error);
      setConversaciones([]);
    }
  };

  const cargarAgentes = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/dashboard/agentes`);
      const data = await response.json();
      setAgentes(Array.isArray(data?.agentes) ? data.agentes : []);
    } catch (error) {
      console.error('Error cargando agentes:', error);
      setAgentes([]);
    }
  };

  const cargarMensajes = async (id) => {
    if (!id) {
      setMensajes([]);
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/api/dashboard/conversacion/${id}`);
      const data = await response.json();
      setMensajes(Array.isArray(data?.mensajes) ? data.mensajes : []);
    } catch (error) {
      console.error('Error cargando mensajes:', error);
      setMensajes([]);
    }
  };

  const cargarDatosCliente = async (telefono) => {
    if (!telefono) {
      setDatosCliente(null);
      return;
    }

    try {
      setDatosCliente(null);

      const response = await authFetch(
        `${API_URL}/api/dashboard/ispcube/cliente/${encodeURIComponent(telefono)}`
      );

      if (response.ok) {
        const data = await response.json();
        setDatosCliente(
          data || {
            plan: 'Sin datos',
            estado: 'Sin datos',
            deuda: 0,
            saldo_a_favor: 0,
          }
        );
      } else {
        setDatosCliente({
          plan: 'Sin datos',
          estado: 'Sin datos',
          deuda: 0,
          saldo_a_favor: 0,
        });
      }
    } catch (error) {
      console.error('Error cargando datos cliente:', error);
      setDatosCliente({
        plan: 'Sin datos',
        estado: 'Sin datos',
        deuda: 0,
        saldo_a_favor: 0,
      });
    }
  };

  const cargarEstadoOperativo = async (id) => {
    if (!id) {
      setEstadoOperativoBackend(null);
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/api/dashboard/conversacion/${id}/estado-operativo`);
      const data = await response.json();
      setEstadoOperativoBackend(data?.estado || null);
    } catch (error) {
      console.error('Error cargando estado operativo:', error);
      setEstadoOperativoBackend(null);
    }
  };

  const guardarEstadoOperativo = async ({
    area,
    campo,
    valor,
    observacion = null,
    proxima_accion = null,
    visita_tecnica = null,
  }) => {
    if (!conversacionActiva?.id || guardandoEstado) return false;

    try {
      setGuardandoEstado(true);

      const response = await authFetch(
        `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/estado-operativo`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            area,
            campo,
            valor,
            observacion,
            proxima_accion,
            visita_tecnica,
            usuario_id: null,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error('Error guardando estado operativo:', data);
        alert(`❌ Error guardando estado: ${data?.error || 'desconocido'}`);
        return false;
      }

      setEstadoOperativoBackend(data?.estado || null);
      return true;
    } catch (error) {
      console.error('Error guardando estado operativo:', error);
      alert('❌ Error de conexión guardando estado');
      return false;
    } finally {
      setGuardandoEstado(false);
    }
  };

  const cargarDatosIniciales = async () => {
    setLoading(true);
    await Promise.all([cargarConversaciones(), cargarAgentes()]);
    setLoading(false);
  };

  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  useEffect(() => {
    if (!conversacionActiva?.id) {
      setMensajes([]);
      setEstadoOperativoBackend(null);
      return;
    }
    cargarMensajes(conversacionActiva.id);
    cargarEstadoOperativo(conversacionActiva.id);
  }, [conversacionActiva?.id]);

  useEffect(() => {
    if (!conversacionActiva?.telefono) {
      setDatosCliente(null);
      return;
    }
    cargarDatosCliente(conversacionActiva.telefono);
  }, [conversacionActiva?.telefono]);

  useEffect(() => {
    const channel = supabase
      .channel('dashboard-conversaciones')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversaciones' },
        () => {
          cargarConversaciones();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      cargarConversaciones();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!conversacionActiva?.id) return;

    const interval = setInterval(() => {
      const activa = conversacionActivaRef.current;
      if (!activa?.id) return;
      cargarMensajes(activa.id);
      cargarEstadoOperativo(activa.id);
    }, 3000);

    return () => clearInterval(interval);
  }, [conversacionActiva?.id]);

  const seleccionarConversacion = (conv) => {
    setConversacionActiva(conv);
    setDiagnosticoBCM(null);
    setMensajeBCM(null);
  };

  // Agente actual: el que seleccionó en el selector de agentes reales
  // Si no seleccionó ninguno, usar el primero disponible como fallback
  const agenteActualId = agenteLogueadoId || null; // null hasta que el agente seleccione su usuario

  const tomarConversacion = async () => {
    if (!conversacionActiva?.id) return;
    if (!agenteActualId) {
      alert('No hay agentes disponibles');
      return;
    }

    try {
      const response = await authFetch(
        `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/tomar`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agente_id: agenteActualId }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        alert(`❌ Error: ${error?.error || 'No se pudo tomar la conversación'}`);
        return;
      }

      alert('✅ Conversación tomada correctamente');
      await cargarConversaciones();
    } catch (error) {
      console.error('Error tomando conversación:', error);
      alert('❌ Error de conexión');
    }
  };

  const transferirConversacion = async () => {
    if (!conversacionActiva?.id) return;

    const categoria = prompt(
      '¿A qué categoría transferir?\n\nOpciones:\n- ventas\n- soporte\n- atencion'
    );

    if (!categoria) return;

    const categoriaNormalizada = categoria.toLowerCase().trim();
    const categoriasValidas = ['ventas', 'soporte', 'atencion'];

    if (!categoriasValidas.includes(categoriaNormalizada)) {
      alert('❌ Categoría inválida. Debe ser: ventas, soporte o atencion');
      return;
    }

    try {
      const response = await authFetch(
        `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/transferir`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categoria: categoriaNormalizada }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        alert(`❌ Error: ${error?.error || 'No se pudo transferir'}`);
        return;
      }

      alert(`✅ Conversación transferida a ${categoriaNormalizada}`);
      await cargarConversaciones();
    } catch (error) {
      console.error('Error transfiriendo conversación:', error);
      alert('❌ Error de conexión');
    }
  };

  const cerrarConversacion = async () => {
    if (!conversacionActiva?.id) return;
    if (!agenteActualId) {
      alert('No hay agentes disponibles');
      return;
    }

    const motivo = prompt(
      'Motivo de cierre:\n\n' +
        '1 - Problema resuelto\n' +
        '2 - Cliente desiste\n' +
        '3 - Derivado a otra área\n' +
        '4 - No responde\n' +
        '5 - Otro\n\n' +
        'Ingresá el número o descripción:'
    );

    if (!motivo) return;

    const motivosMap = {
      '1': 'Problema resuelto',
      '2': 'Cliente desiste',
      '3': 'Derivado a otra área',
      '4': 'No responde',
      '5': 'Otro',
    };

    const motivoFinal = motivosMap[motivo] || motivo;

    if (!window.confirm(`¿Cerrar esta conversación?\n\nMotivo: ${motivoFinal}`)) return;

    try {
      const response = await authFetch(
        `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/cerrar`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            motivo: motivoFinal,
            agente_id: agenteActualId,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        alert(`❌ Error: ${error?.error || 'No se pudo cerrar'}`);
        return;
      }

      alert('✅ Conversación cerrada correctamente');
      setConversacionActiva(null);
      setMensajes([]);
      setDatosCliente(null);
      setEstadoOperativoBackend(null);
      await cargarConversaciones();
    } catch (error) {
      console.error('Error cerrando conversación:', error);
      alert('❌ Error de conexión');
    }
  };

  const toggleIA = async () => {
    if (!conversacionActiva?.id || cambiandoIA) return;
    if (!agenteActualId) {
      alert('No hay agentes disponibles');
      return;
    }

    const iaEstaActiva = conversacionActiva.ia_activa !== false;
    const activar = !iaEstaActiva;

    const confirmacion = window.confirm(
      activar
        ? '¿Reactivar IA en esta conversación?'
        : '¿Anular IA en esta conversación?'
    );

    if (!confirmacion) return;

    try {
      setCambiandoIA(true);

      const response = await authFetch(
        `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/toggle-ia`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ia_activa: activar,
            agente_id: agenteActualId,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        alert(`❌ Error: ${error?.error || 'No se pudo cambiar el estado de IA'}`);
        return;
      }

      await cargarConversaciones();
      await cargarMensajes(conversacionActiva.id);

      const responseConv = await authFetch(
        `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}`
      );
      const dataConv = await responseConv.json();

      if (dataConv?.conversacion) {
        setConversacionActiva(dataConv.conversacion);
      }

      alert(activar ? '✅ IA reactivada' : '✅ IA anulada');
    } catch (error) {
      console.error('Error cambiando IA:', error);
      alert('❌ Error de conexión');
    } finally {
      setCambiandoIA(false);
    }
  };

  const enviarMensaje = async () => {
    if (!conversacionActiva?.id) return;

    const contenido = (nuevoMensaje || '').trim();
    if (!contenido) return;

    if (!agenteActualId) {
      alert('No hay agentes disponibles');
      return;
    }

    try {
      setEnviandoMensaje(true);

      const response = await authFetch(
        `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/mensaje`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contenido,
            agente_id: agenteActualId,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        alert(`❌ Error: ${data?.error || 'No se pudo enviar el mensaje'}`);
        return;
      }

      setNuevoMensaje('');
      await cargarMensajes(conversacionActiva.id);
      await cargarConversaciones();
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      alert('❌ Error de conexión enviando mensaje');
    } finally {
      setEnviandoMensaje(false);
    }
  };

  const actualizarVentaLocal = (convId, patch) => {
    if (!convId) return;
    setVentasLocalData((prev) => ({
      ...prev,
      [convId]: {
        ...prev[convId],
        ...patch,
      },
    }));
  };

  const actualizarAtencionLocal = (convId, patch) => {
    if (!convId) return;
    setAtencionLocalData((prev) => ({
      ...prev,
      [convId]: {
        ...prev[convId],
        ...patch,
      },
    }));
  };

  const actualizarSoporteLocal = (convId, patch) => {
    if (!convId) return;
    setSoporteLocalData((prev) => ({
      ...prev,
      [convId]: {
        ...prev[convId],
        ...patch,
      },
    }));
  };

  const obtenerVentaLocal = (conv) => {
    if (!conv?.id) return null;
    const saved = ventasLocalData[conv.id] || {};
    return {
      etapa: saved.etapa || 'Lead nuevo',
      proximaAccion: saved.proximaAccion || 'Revisar resumen interno',
      origen: saved.origen || 'WhatsApp',
      cobertura: saved.cobertura || 'Pendiente de validar',
      prioridadComercial: saved.prioridadComercial || 'Media',
      planSugerido: saved.planSugerido || datosCliente?.plan || '300 Mb',
      resumenInterno: {
        ...DEFAULT_SALES_SUMMARY,
        ...(saved.resumenInterno || {}),
      },
      ultimaGestion: saved.ultimaGestion || null,
    };
  };

  const obtenerAtencionLocal = (conv) => {
    if (!conv?.id) return null;
    const saved = atencionLocalData[conv.id] || {};
    return {
      estadoAdministrativo: saved.estadoAdministrativo || 'Pendiente de revisión',
      proximaAccion: saved.proximaAccion || 'Enviar factura al cliente',
      observacion: saved.observacion || '',
    };
  };

  const obtenerSoporteLocal = (conv) => {
    if (!conv?.id) return null;
    const saved = soporteLocalData[conv.id] || {};
    return {
      estadoTecnico: saved.estadoTecnico || 'Pendiente de revisión',
      diagnostico: saved.diagnostico || 'Sin señal óptica',
      proximaAccion: saved.proximaAccion || 'Revisar ONU y validar visita',
      visitaTecnica: saved.visitaTecnica || 'No',
      observacion: saved.observacion || '',
    };
  };

  const obtenerVenta = (conv) => {
    const local = obtenerVentaLocal(conv);
    if (!conv?.id) return local;

    return {
      etapa: estadoOperativoBackend?.etapa_ventas || local?.etapa || 'Lead nuevo',
      proximaAccion:
        estadoOperativoBackend?.proxima_accion || local?.proximaAccion || 'Revisar resumen interno',
      origen: local?.origen || 'WhatsApp',
      cobertura: local?.cobertura || 'Pendiente de validar',
      prioridadComercial: local?.prioridadComercial || 'Media',
      planSugerido: local?.planSugerido || datosCliente?.plan || '300 Mb',
      observacion: estadoOperativoBackend?.observacion || '',
      resumenInterno: local?.resumenInterno || { ...DEFAULT_SALES_SUMMARY },
      ultimaGestion: local?.ultimaGestion || null,
    };
  };

  const obtenerAtencion = (conv) => {
    const local = obtenerAtencionLocal(conv);
    if (!conv?.id) return local;

    return {
      estadoAdministrativo:
        estadoOperativoBackend?.estado_atencion ||
        local?.estadoAdministrativo ||
        'Pendiente de revisión',
      proximaAccion:
        estadoOperativoBackend?.proxima_accion ||
        local?.proximaAccion ||
        'Enviar factura al cliente',
      observacion: estadoOperativoBackend?.observacion || local?.observacion || '',
    };
  };

  const obtenerSoporte = (conv) => {
    const local = obtenerSoporteLocal(conv);
    if (!conv?.id) return local;

    return {
      estadoTecnico:
        estadoOperativoBackend?.estado_soporte ||
        local?.estadoTecnico ||
        'Pendiente de revisión',
      diagnostico: local?.diagnostico || 'Sin señal óptica',
      proximaAccion:
        estadoOperativoBackend?.proxima_accion ||
        local?.proximaAccion ||
        'Revisar ONU y validar visita',
      visitaTecnica:
        typeof estadoOperativoBackend?.visita_tecnica === 'boolean'
          ? estadoOperativoBackend.visita_tecnica
            ? 'Sí'
            : 'No'
          : local?.visitaTecnica || 'No',
      observacion: estadoOperativoBackend?.observacion || local?.observacion || '',
    };
  };



  // ── Soporte BCM: funciones de acción real ────────────────────────────────

  const ejecutarDiagnostico = async () => {
    if (!conversacionActiva?.id || ejecutandoAccionBCM) return;
    try {
      setEjecutandoAccionBCM(true);
      setMensajeBCM('Consultando BCM...');
      setDiagnosticoBCM(null);
      const resp = await authFetch(`${API_URL}/api/dashboard/bcm/diagnostico/${conversacionActiva.id}`);
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) { setMensajeBCM(`❌ ${data?.error || 'Error consultando BCM'}`); return; }
      setDiagnosticoBCM(data.diagnostico);
      actualizarSoporteLocal(conversacionActiva.id, {
        estadoTecnico: 'Diagnóstico en curso',
        diagnostico: data.diagnostico?.diagnostico_resumen || 'Sin datos',
      });
      await guardarEstadoOperativo({
        area: 'soporte', campo: 'estado_soporte', valor: 'Diagnóstico en curso',
        proxima_accion: data.diagnostico?.problema_fisico ? 'Problema físico — generar ticket' : 'Evaluar reinicio o desbloqueo',
        observacion: data.diagnostico?.diagnostico_resumen || null,
      });
      setMensajeBCM(data.diagnostico?.problema_fisico
        ? '⚠️ ONU sin potencia óptica (ESTADO=0). Problema físico. Generá ticket ISPCube.'
        : `✅ ONU online. ${data.diagnostico?.diagnostico_resumen || ''}`);
    } catch (e) {
      setMensajeBCM('❌ Error de conexión con BCM');
    } finally {
      setEjecutandoAccionBCM(false);
    }
  };

  const reiniciarONU = async () => {
    if (!conversacionActiva?.id || ejecutandoAccionBCM) return;
    const serial = diagnosticoBCM?.serial;
    if (!serial) { alert('❌ Ejecutá primero el diagnóstico para obtener el serial de la ONU.'); return; }
    if (!window.confirm(`¿Reiniciar ONU remotamente?\n\nSerial: ${serial}\n\nEl equipo tardará 2-3 minutos en volver.`)) return;
    try {
      setEjecutandoAccionBCM(true);
      setReinicioConfirmado(null);

      // Capturar FECHA_POTENCIA antes del reinicio
      let fechaAntes = null;
      try {
        const respAntes = await authFetch(`${API_URL}/api/dashboard/bcm/estado-serial/${serial}`);
        const dataAntes = await respAntes.json().catch(() => ({}));
        fechaAntes = dataAntes?.FECHA_POTENCIA || null;
      } catch (e) {}

      setMensajeBCM('Enviando reinicio ONU...');
      const resp = await authFetch(`${API_URL}/api/dashboard/bcm/reiniciar/${conversacionActiva.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serial, agente_id: agenteActualId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) { setMensajeBCM(`❌ ${data?.error || 'Error reiniciando ONU'}`); return; }

      setMensajeBCM('⏳ Reinicio enviado. Verificando reconexión en 3 minutos...');

      // Verificar automáticamente a los 3 minutos
      setTimeout(async () => {
        try {
          setMensajeBCM('🔍 Verificando reconexión...');
          const respDespues = await authFetch(`${API_URL}/api/dashboard/bcm/estado-serial/${serial}`);
          const dataDespues = await respDespues.json().catch(() => ({}));
          const fechaDespues = dataDespues?.FECHA_POTENCIA || null;
          const estadoDespues = dataDespues?.ESTADO;

          if (fechaDespues && fechaAntes && fechaDespues !== fechaAntes) {
            setReinicioConfirmado({ confirmado: true, fecha: fechaDespues, potencia: dataDespues?.POTENCIA });
            setMensajeBCM(`✅ Reinicio confirmado. Reconectó a las ${fechaDespues.split(' ')[1]}`);
          } else if (estadoDespues === '1' || estadoDespues === 1) {
            setReinicioConfirmado({ confirmado: true, fecha: fechaDespues, potencia: dataDespues?.POTENCIA });
            setMensajeBCM(`✅ ONU online. Potencia: ${dataDespues?.POTENCIA || '-'} dBm`);
          } else {
            setReinicioConfirmado({ confirmado: false, fecha: fechaDespues });
            setMensajeBCM('⚠️ Sin reconexión todavía. Podés verificar manualmente.');
          }
        } catch (e) {
          setMensajeBCM('⚠️ Reinicio enviado. No se pudo verificar reconexión automáticamente.');
        }
      }, 3 * 60 * 1000);

    } catch (e) {
      setMensajeBCM('❌ Error de conexión');
    } finally {
      setEjecutandoAccionBCM(false);
    }
  };

  const verificarReinicioManual = async () => {
    const serial = diagnosticoBCM?.serial;
    if (!serial) return;
    try {
      setMensajeBCM('🔍 Verificando estado ONU...');
      const resp = await authFetch(`${API_URL}/api/dashboard/bcm/estado-serial/${serial}`);
      const data = await resp.json().catch(() => ({}));
      if (data?.ESTADO === '1' || data?.ESTADO === 1) {
        setReinicioConfirmado({ confirmado: true, fecha: data.FECHA_POTENCIA, potencia: data.POTENCIA });
        setMensajeBCM(`✅ ONU online. Potencia: ${data.POTENCIA || '-'} dBm. Última reconexión: ${data.FECHA_POTENCIA || '-'}`);
      } else {
        setMensajeBCM('⚠️ ONU todavía sin reconectar.');
      }
    } catch (e) {
      setMensajeBCM('❌ Error verificando estado');
    }
  };

  const desbloquearBCM = async () => {
    if (!conversacionActiva?.id || ejecutandoAccionBCM) return;
    if (!window.confirm('¿Desbloquear cliente en BCM?\n\nSolo se ejecuta si ISPCube ya lo tiene habilitado.')) return;
    try {
      setEjecutandoAccionBCM(true);
      setMensajeBCM('Verificando estado en ISPCube...');
      const resp = await authFetch(`${API_URL}/api/dashboard/bcm/desbloquear/${conversacionActiva.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agente_id: agenteActualId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 409) { setMensajeBCM(`🔒 ${data?.mensaje || 'Cliente todavía bloqueado en ISPCube.'}`); return; }
      if (!resp.ok) { setMensajeBCM(`❌ ${data?.error || 'Error desbloqueando en BCM'}`); return; }
      setMensajeBCM(data.mensaje || '✅ Cliente desbloqueado en BCM.');
      await guardarEstadoOperativo({
        area: 'soporte', campo: 'estado_soporte', valor: 'Diagnóstico en curso',
        proxima_accion: 'Confirmar conexión con el cliente',
        observacion: `Desbloqueado en BCM. ISPCube status=${data.status_ispcube}`,
      });
    } catch (e) {
      setMensajeBCM('❌ Error de conexión');
    } finally {
      setEjecutandoAccionBCM(false);
    }
  };

  const confirmarSoporte = async () => {
    if (!conversacionActiva?.id || ejecutandoAccionBCM) return;
    if (!agenteActualId) { alert('No hay agentes disponibles'); return; }
    const problema = soporteActual?.diagnostico || prompt('Describí el problema técnico:');
    if (!problema) return;
    if (!window.confirm(`¿Generar ticket de soporte en ISPCube y cerrar conversación?\n\nProblema: ${problema}`)) return;
    try {
      setEjecutandoAccionBCM(true);
      setMensajeBCM('Generando ticket en ISPCube...');
      const resp = await authFetch(`${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/confirmar-soporte`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agente_id: agenteActualId,
          agente_nombre: agentes.find((a) => a.id === agenteActualId)?.nombre || currentUser?.nombre || 'Agente',
          problema,
          observaciones: soporteActual?.observacion || null,
          serial: diagnosticoBCM?.serial || null,
          idDeviceTR: diagnosticoBCM?.idDeviceTR || null,
          estado_onu: diagnosticoBCM?.estado_onu ?? null,
          rx_power: diagnosticoBCM?.rx_power || null,
          diagnostico_resumen: diagnosticoBCM?.diagnostico_resumen || null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) { setMensajeBCM(`❌ ${data?.error || 'Error generando ticket'}`); alert(`❌ ${data?.error || 'No se pudo generar el ticket'}`); return; }
      const ref = data.ticket?.referencia;
      alert(`✅ Ticket generado${ref ? ` #${ref}` : ''}. Conversación cerrada.`);
      setConversacionActiva(null); setMensajes([]); setDatosCliente(null);
      setEstadoOperativoBackend(null); setDiagnosticoBCM(null); setMensajeBCM(null);
      await cargarConversaciones();
    } catch (e) {
      setMensajeBCM('❌ Error de conexión');
    } finally {
      setEjecutandoAccionBCM(false);
    }
  };

  const extraerEmailMensajes = (listaMensajes = []) => {
    for (const msg of listaMensajes) {
      const contenido = String(msg?.contenido || '');
      const match = contenido.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
      if (match) return match[0];
    }
    return '';
  };

  const extraerDocumentoMensajes = (listaMensajes = []) => {
    for (const msg of listaMensajes) {
      const contenido = String(msg?.contenido || '').replace(/\D/g, '');
      if (contenido.length >= 11 && contenido.length <= 13) return contenido;
    }
    return '';
  };

  const extraerDireccionMensajes = (listaMensajes = []) => {
    for (const msg of listaMensajes) {
      if (msg?.rol !== 'cliente') continue;
      const contenido = String(msg?.contenido || '').trim();
      if (!contenido || contenido.includes('@')) continue;
      if (/^\d{10,13}$/.test(contenido.replace(/\D/g, ''))) continue;
      if (/\d/.test(contenido) && /[a-záéíóúñ]/i.test(contenido) && contenido.length >= 8) {
        return contenido;
      }
    }
    return '';
  };

  const extraerPrecioMensajes = (listaMensajes = []) => {
    for (let i = listaMensajes.length - 1; i >= 0; i -= 1) {
      const contenido = String(listaMensajes[i]?.contenido || '');
      const match = contenido.match(/\$\s*([\d\.]+)/);
      if (match) return match[1].replace(/\./g, '');
    }
    return '';
  };

  const extraerMedioPagoMensajes = (listaMensajes = []) => {
    for (let i = listaMensajes.length - 1; i >= 0; i -= 1) {
      const contenido = String(listaMensajes[i]?.contenido || '').toLowerCase();
      if (contenido.includes('mercado')) return 'Mercado Pago';
      if (contenido.includes('transfer')) return 'Transferencia';
      if (contenido.includes('pago fácil') || contenido.includes('pagofacil')) return 'Pago Fácil';
      if (contenido.includes('tarjeta')) return 'Tarjeta';
    }
    return '';
  };

  const construirResumenVenta = (overrideData = {}) => {
    const resumenGuardado = ventaActual?.resumenInterno || {};
    const base = crearFormularioVentaInicial({
      conversacion: conversacionActiva,
      mensajes,
      datosCliente,
      ventaActual,
      resumenGuardado,
      agenteNombre:
        agentes.find((a) => a.id === agenteActualId)?.nombre || currentUser?.nombre || 'Agente',
    });

    const merged = {
      ...base,
      ...overrideData,
    };

    return {
      conversacion_id: conversacionActiva?.id || '',
      cliente_nombre: normalizarTexto(merged.nombre_apellido),
      telefono: normalizarTelefonoVisual(merged.telefono || conversacionActiva?.telefono || ''),
      email: normalizarTexto(merged.email),
      cuil_dni: normalizarTexto(merged.cuil),
      direccion: normalizarTexto(merged.direccion),
      entre_calle_1: normalizarTexto(merged.entre_calle_1),
      entre_calle_2: normalizarTexto(merged.entre_calle_2),
      localidad: normalizarTexto(merged.localidad),
      plan: normalizarTexto(merged.plan || ventaActual?.planSugerido || ''),
      precio: normalizarTexto(merged.precio),
      medio_pago: normalizarTexto(merged.medio_pago),
      coordenadas:
        normalizarTexto(merged.latitud) && normalizarTexto(merged.longitud)
          ? {
              lat: normalizarTexto(merged.latitud),
              lng: normalizarTexto(merged.longitud),
            }
          : null,
      mapa_url: normalizarTexto(merged.mapa_url),
      agente_id: agenteActualId,
      agente_nombre:
        agentes.find((a) => a.id === agenteActualId)?.nombre || currentUser?.nombre || 'Agente',
      observaciones: normalizarTexto(merged.observaciones),
    };
  };

  const persistirResumenVenta = async (overrideData = {}, patchExtra = {}) => {
    if (!conversacionActiva?.id) return null;
    const resumen = construirResumenVenta(overrideData);

    let coordenadas = resumen?.coordenadas || null;
    let mapaUrl = resumen?.mapa_url || '';
    let direccionCorregida = resumen?.direccion || '';
    let localidadCorregida = resumen?.localidad || '';

    console.log('[GEOCODE DEBUG] resumen inicial:', { direccion: resumen?.direccion, localidad: resumen?.localidad, coordenadas: resumen?.coordenadas });

    // Validar que las coordenadas sean números válidos, no strings vacíos
    const latValida = coordenadas?.lat && !isNaN(parseFloat(coordenadas.lat));
    const lngValida = coordenadas?.lng && !isNaN(parseFloat(coordenadas.lng));
    console.log('[GEOCODE DEBUG] Coordenadas válidas?', { latValida, lngValida });

    if (!latValida || !lngValida) {
      console.log('[GEOCODE DEBUG] Llamando endpoint con:', {
        direccion: resumen?.direccion,
        entre_calle_1: resumen?.entre_calle_1,
        entre_calle_2: resumen?.entre_calle_2,
        localidad: resumen?.localidad,
      });
      const geocodeResponse = await geocodificarResumenVentaFrontend({
        direccion: resumen?.direccion,
        entre_calle_1: resumen?.entre_calle_1,
        entre_calle_2: resumen?.entre_calle_2,
        localidad: resumen?.localidad,
      });
      console.log('[GEOCODE DEBUG] Respuesta del endpoint:', geocodeResponse);

      const geocode = geocodeResponse?.coordenadas;
      if (geocode?.lat && geocode?.lng) {
        coordenadas = { lat: String(geocode.lat), lng: String(geocode.lng) };
        mapaUrl = geocode?.google_maps_url || '';
        console.log('[GEOCODE DEBUG] Coordenadas asignadas:', coordenadas);
        // Usar dirección y localidad canónicas si vinieron del geocode
        if (geocodeResponse?.direccion_canonica) direccionCorregida = geocodeResponse.direccion_canonica;
        if (geocodeResponse?.localidad_canonica) localidadCorregida = geocodeResponse.localidad_canonica;
      } else {
        console.log('[GEOCODE DEBUG] Geocode SIN coordenadas válidas');
      }
    }

    const resumenInternoFinal = {
      ...DEFAULT_SALES_SUMMARY,
      ...(ventaActual?.resumenInterno || {}),
      ...overrideData,
      direccion: direccionCorregida || overrideData?.direccion || ventaActual?.resumenInterno?.direccion || '',
      localidad: localidadCorregida || overrideData?.localidad || ventaActual?.resumenInterno?.localidad || '',
      latitud: coordenadas?.lat || overrideData?.latitud || ventaActual?.resumenInterno?.latitud || '',
      longitud: coordenadas?.lng || overrideData?.longitud || ventaActual?.resumenInterno?.longitud || '',
      mapa_url: mapaUrl || overrideData?.mapa_url || ventaActual?.resumenInterno?.mapa_url || '',
    };

    actualizarVentaLocal(conversacionActiva.id, {
      resumenInterno: resumenInternoFinal,
      planSugerido: resumen.plan || ventaActual?.planSugerido || '',
      ...patchExtra,
      ultimaGestion: new Date().toISOString(),
    });

    return {
      ...resumen,
      direccion: direccionCorregida || resumen.direccion,
      localidad: localidadCorregida || resumen.localidad,
      coordenadas,
      mapa_url: mapaUrl,
    };
  };

  const abrirModalVentas = (type) => {
    if (!conversacionActiva?.id) return;

    if (type === 'confirmar') {
      // Extraer datos desde mensajes (fuente primaria)
      // resumenInterno actúa como override si el agente ya guardó algo antes
      const resumenBase = crearFormularioVentaInicial({
        conversacion: conversacionActiva,
        mensajes,
        datosCliente,
        ventaActual,
        resumenGuardado: ventaActual?.resumenInterno || {},
        agenteNombre:
          agentes.find((a) => a.id === agenteActualId)?.nombre || currentUser?.nombre || 'Agente',
      });
      setSalesModal({ open: true, type });
      setSalesModalForm({
        ...resumenBase,
        proxima_accion: ventaActual?.proximaAccion || 'Pendiente de instalación',
      });
      return;
    }

    const resumenBase = crearFormularioVentaInicial({
      conversacion: conversacionActiva,
      mensajes,
      datosCliente,
      ventaActual,
      resumenGuardado: ventaActual?.resumenInterno || {},
      agenteNombre:
        agentes.find((a) => a.id === agenteActualId)?.nombre || currentUser?.nombre || 'Agente',
    });

    const presets = {
      contactar: {
        observacion: ventaActual?.observacion || '',
      },
      calificar: {
        observacion: ventaActual?.observacion || '',
      },
      cobertura: {
        direccion: resumenBase.direccion,
        localidad: resumenBase.localidad,
        observacion: ventaActual?.observacion || '',
        cobertura_resultado:
          ventaActual?.cobertura === 'Sin cobertura expansión'
            ? 'sin_cobertura_expansion'
            : ventaActual?.cobertura === 'Sin cobertura'
            ? 'sin_cobertura'
            : 'cobertura_ok',
      },
      propuesta: {
        plan: resumenBase.plan,
        precio: resumenBase.precio,
        medio_pago: resumenBase.medio_pago,
        observacion: ventaActual?.observacion || '',
      },
      seguimiento: {
        proxima_accion: ventaActual?.proximaAccion || '',
        fecha_seguimiento: '',
        observacion: ventaActual?.observacion || '',
      },
      aceptar: {
        plan: resumenBase.plan,
        precio: resumenBase.precio,
        observacion: ventaActual?.observacion || '',
      },
      perdida: {
        motivo_perdida: '',
        observacion: '',
      },
    };

    setSalesModal({ open: true, type });
    setSalesModalForm(presets[type] || {});
  };

   

  const cerrarModalVentas = () => {
    setSalesModal({ open: false, type: null });
    setSalesModalForm({});
    setSubmittingSalesModal(false);
  };

  const guardarCambioVentas = async ({
    etapa,
    proximaAccion,
    observacion = '',
    patch = {},
  }) => {
    if (!conversacionActiva?.id) return false;
    actualizarVentaLocal(conversacionActiva.id, {
      etapa,
      proximaAccion,
      ...patch,
      ultimaGestion: new Date().toISOString(),
    });

    return guardarEstadoOperativo({
      area: 'ventas',
      campo: 'etapa_ventas',
      valor: etapa,
      proxima_accion: proximaAccion,
      observacion: observacion || null,
    });
  };

  const guardarResumenVenta = async (overrideData = {}) => {
    if (!conversacionActiva?.id) return false;

    try {
      setSubmittingSalesModal(true);
      const resumen = await persistirResumenVenta(overrideData, {
        etapa: ventaActual?.etapa || 'Lead nuevo',
        proximaAccion: ventaActual?.proximaAccion || 'Revisar resumen interno',
      });

      const nombreCompleto = normalizarTexto(resumen?.cliente_nombre);
      const localidad = normalizarTexto(resumen?.localidad);

      await guardarCambioVentas({
        etapa: ventaActual?.etapa || 'Lead nuevo',
        proximaAccion:
          nombreCompleto && localidad
            ? 'Resumen interno listo para confirmación'
            : 'Completar datos faltantes para cierre',
        observacion: resumen?.observaciones || ventaActual?.observacion || 'Resumen interno actualizado',
        patch: {
          resumenInterno: {
            ...DEFAULT_SALES_SUMMARY,
            ...(ventaActual?.resumenInterno || {}),
            ...overrideData,
            latitud: resumen?.coordenadas?.lat || overrideData?.latitud || ventaActual?.resumenInterno?.latitud || '',
            longitud: resumen?.coordenadas?.lng || overrideData?.longitud || ventaActual?.resumenInterno?.longitud || '',
            mapa_url: resumen?.mapa_url || overrideData?.mapa_url || ventaActual?.resumenInterno?.mapa_url || '',
          },
          planSugerido: resumen?.plan || ventaActual?.planSugerido || '',
        },
      });

      cerrarModalVentas();
      alert('✅ Resumen interno guardado. La venta todavía no fue confirmada.');
      return true;
    } catch (error) {
      console.error('Error guardando resumen interno:', error);
      alert('❌ Error guardando resumen interno');
      return false;
    } finally {
      setSubmittingSalesModal(false);
    }
  };

  const confirmarVenta = async (overrideData = {}) => {
    if (!conversacionActiva?.id) return false;
    if (!agenteActualId) {
      alert('No hay agentes disponibles');
      return false;
    }

    try {
      setSubmittingSalesModal(true);
      const resumen = await persistirResumenVenta(overrideData, {
        etapa: 'Venta confirmada',
        proximaAccion: 'Esperando gestión de implementación',
        cobertura: 'Cobertura OK',
      });

      const faltantes = [];
      if (!normalizarTexto(resumen?.cliente_nombre)) faltantes.push('Nombre y apellido');
      if (!normalizarTexto(resumen?.telefono)) faltantes.push('Teléfono');
      if (!normalizarTexto(resumen?.direccion)) faltantes.push('Dirección');
      if (!normalizarTexto(resumen?.localidad)) faltantes.push('Localidad');
      if (!normalizarTexto(resumen?.plan)) faltantes.push('Plan');

      if (faltantes.length) {
        alert(`⚠️ Antes de confirmar la venta completá: ${faltantes.join(', ')}`);
        return false;
      }

      // Incluir coordenadas del geocode en el payload final
      console.log('[CONFIRMAR DEBUG] resumen recibido de persistirResumenVenta:', resumen);
      const payloadFinal = {
        ...resumen,
        coordenadas: resumen.coordenadas || null,
        latitud: resumen.coordenadas?.lat || '',
        longitud: resumen.coordenadas?.lng || '',
        mapa_url: resumen.mapa_url || '',
      };
      console.log('[CONFIRMAR DEBUG] payloadFinal que va al backend:', payloadFinal);

      const response = await authFetch(
        `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/confirmar-venta`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadFinal),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        alert(`❌ Error: ${data?.error || 'No se pudo confirmar la venta'}`);
        return false;
      }

      await guardarCambioVentas({
        etapa: 'Venta confirmada',
        proximaAccion: 'Esperando gestión de implementación',
        observacion: resumen.observaciones || 'Venta confirmada por agente',
        patch: {
          cobertura: 'Cobertura OK',
          planSugerido: resumen.plan || ventaActual?.planSugerido || '',
        },
      });

      await cargarConversaciones();
      setConversacionActiva(null);
      setMensajes([]);
      setEstadoOperativoBackend(null);
      setDatosCliente(null);
      cerrarModalVentas();
      alert('✅ Venta confirmada y enviada a Telegram');
      return true;
    } catch (error) {
      console.error('Error confirmando venta:', error);
      alert('❌ Error de conexión confirmando venta');
      return false;
    } finally {
      setSubmittingSalesModal(false);
    }
  };

  const guardarAccionVentasModal = async () => {
    if (!conversacionActiva?.id) return;
    const tipo = salesModal.type;
    if (!tipo) return;

    try {
      setSubmittingSalesModal(true);

      if (tipo === 'confirmar') {
        await guardarResumenVenta(salesModalForm);
        return;
      }

      if (tipo === 'contactar') {
        await guardarCambioVentas({
          etapa: 'Contactado',
          proximaAccion: salesModalForm.proxima_accion || 'Continuar calificación comercial',
          observacion: salesModalForm.observacion || 'Contacto inicial realizado',
        });
      }

      if (tipo === 'calificar') {
        const etapaDestino = salesModalForm.resultado_calificacion === 'descartar' ? 'Venta perdida' : 'Calificado';
        await guardarCambioVentas({
          etapa: etapaDestino,
          proximaAccion:
            etapaDestino === 'Venta perdida'
              ? 'Caso cerrado'
              : salesModalForm.proxima_accion || 'Validar cobertura',
          observacion: salesModalForm.observacion || '',
        });
      }

      if (tipo === 'cobertura') {
        await persistirResumenVenta({
          direccion: salesModalForm.direccion || '',
          entre_calle_1: salesModalForm.entre_calle_1 || '',
          entre_calle_2: salesModalForm.entre_calle_2 || '',
          localidad: salesModalForm.localidad || '',
          observaciones: salesModalForm.observacion || ventaActual?.resumenInterno?.observaciones || '',
        });

        let etapaDestino = 'Cobertura validada';
        let proximaAccion = 'Enviar propuesta';
        let cobertura = 'Cobertura OK';

        if (salesModalForm.cobertura_resultado === 'sin_cobertura_expansion') {
          etapaDestino = 'Sin cobertura expansión';
          proximaAccion = 'Esperar avance de red';
          cobertura = 'Sin cobertura expansión';
        } else if (salesModalForm.cobertura_resultado === 'sin_cobertura') {
          etapaDestino = 'Venta perdida';
          proximaAccion = 'Caso cerrado';
          cobertura = 'Sin cobertura';
        }

        await guardarCambioVentas({
          etapa: etapaDestino,
          proximaAccion,
          observacion: salesModalForm.observacion || '',
          patch: {
            cobertura,
          },
        });
      }

      if (tipo === 'propuesta') {
        await persistirResumenVenta({
          plan: salesModalForm.plan || '',
          precio: salesModalForm.precio || '',
          medio_pago: salesModalForm.medio_pago || '',
          observaciones:
            salesModalForm.observacion || ventaActual?.resumenInterno?.observaciones || '',
        }, {
          planSugerido: salesModalForm.plan || ventaActual?.planSugerido || '',
        });

        await guardarCambioVentas({
          etapa: 'Propuesta enviada',
          proximaAccion: salesModalForm.proxima_accion || 'Hacer seguimiento comercial',
          observacion: salesModalForm.observacion || '',
          patch: {
            planSugerido: salesModalForm.plan || ventaActual?.planSugerido || '',
          },
        });
      }

      if (tipo === 'seguimiento') {
        await guardarCambioVentas({
          etapa: 'Seguimiento',
          proximaAccion: salesModalForm.proxima_accion || 'Recontactar cliente',
          observacion: salesModalForm.observacion || '',
        });
      }

      if (tipo === 'aceptar') {
        await persistirResumenVenta({
          plan: salesModalForm.plan || '',
          precio: salesModalForm.precio || '',
          observaciones:
            salesModalForm.observacion || ventaActual?.resumenInterno?.observaciones || '',
        }, {
          planSugerido: salesModalForm.plan || ventaActual?.planSugerido || '',
        });

        await guardarCambioVentas({
          etapa: 'Aceptó oferta',
          proximaAccion: 'Validar datos finales y confirmar venta',
          observacion: salesModalForm.observacion || '',
          patch: {
            planSugerido: salesModalForm.plan || ventaActual?.planSugerido || '',
          },
        });
      }

      if (tipo === 'perdida') {
        await guardarCambioVentas({
          etapa: 'Venta perdida',
          proximaAccion: 'Caso cerrado',
          observacion: `${salesModalForm.motivo_perdida || 'Sin motivo'}${salesModalForm.observacion ? ` · ${salesModalForm.observacion}` : ''}`,
        });
      }

      await cargarConversaciones();
      cerrarModalVentas();
    } catch (error) {
      console.error('Error guardando acción de ventas:', error);
      alert('❌ No se pudo guardar la acción comercial');
      setSubmittingSalesModal(false);
    }
  };

  const accionRapidaVentas = async (accion) => {
    const acciones = {
      contactar: 'contactar',
      calificar: 'calificar',
      cobertura: 'cobertura',
      propuesta: 'propuesta',
      seguimiento: 'seguimiento',
      aceptar: 'aceptar',
      perdida: 'perdida',
      confirmar: 'confirmar',
    };

    const tipo = acciones[accion];
    if (!tipo) return;
    abrirModalVentas(tipo);
  };

  const accionRapidaAtencion = async (accion) => {
    if (!conversacionActiva?.id) return;

    const map = {
      factura: {
        estadoAdministrativo: 'Factura enviada',
        proximaAccion: 'Esperar confirmación del cliente',
      },
      pago: {
        estadoAdministrativo: 'Esperando pago',
        proximaAccion: 'Verificar acreditación',
      },
      resuelto: {
        estadoAdministrativo: 'Resuelto',
        proximaAccion: 'Sin acción pendiente',
      },
    };

    const next = map[accion];
    if (!next) return;

    actualizarAtencionLocal(conversacionActiva.id, {
      estadoAdministrativo: next.estadoAdministrativo,
      proximaAccion: next.proximaAccion,
    });

    await guardarEstadoOperativo({
      area: 'atencion',
      campo: 'estado_atencion',
      valor: next.estadoAdministrativo,
      proxima_accion: next.proximaAccion,
      observacion: obtenerAtencion(conversacionActiva)?.observacion || null,
    });
  };

  const accionRapidaSoporte = async (accion) => {
    if (!conversacionActiva?.id) return;

    const map = {
      diagnostico: {
        estadoTecnico: 'Diagnóstico en curso',
        proximaAccion: 'Validar mediciones y descartar falla física',
        visitaTecnica: 'No',
      },
      visita: {
        estadoTecnico: 'Derivado a visita técnica',
        proximaAccion: 'Coordinar visita con cliente',
        visitaTecnica: 'Sí',
      },
      resuelto: {
        estadoTecnico: 'Resuelto',
        proximaAccion: 'Sin acción pendiente',
        visitaTecnica: 'No',
      },
    };

    const next = map[accion];
    if (!next) return;

    actualizarSoporteLocal(conversacionActiva.id, {
      estadoTecnico: next.estadoTecnico,
      proximaAccion: next.proximaAccion,
      visitaTecnica: next.visitaTecnica,
    });

    await guardarEstadoOperativo({
      area: 'soporte',
      campo: 'estado_soporte',
      valor: next.estadoTecnico,
      proxima_accion: next.proximaAccion,
      visita_tecnica: next.visitaTecnica === 'Sí',
      observacion: obtenerSoporte(conversacionActiva)?.observacion || null,
    });
  };

  const conversacionesPorVista = useMemo(() => {
    return conversaciones.filter((c) => {
      if (!tieneAccesoArea(c.categoria || c.area)) return false;
      if (vistaActiva === 'historial') return c.estado === 'cerrada';
      return ESTADOS_ACTIVOS.includes(c.estado);
    });
  }, [conversaciones, vistaActiva, selectedDemoUserId]);

  const conversacionesFiltradas = useMemo(() => {
    return conversacionesPorVista.filter((c) => {
      const nombre = c.nombre_cliente?.toLowerCase() || '';
      const telefono = c.telefono || '';
      const busquedaNormalizada = busqueda.toLowerCase().trim();

      const coincideBusqueda =
        busquedaNormalizada === '' ||
        nombre.includes(busquedaNormalizada) ||
        telefono.includes(busquedaNormalizada);

      if (!coincideBusqueda) return false;

      if (filtro === 'todas') return true;
      if (filtro === 'pendientes') return !c.agente_id && c.estado !== 'cerrada';
      if (filtro === 'criticas') return c.prioridad === 'critica';
      if (filtro === 'derivadas') return !!c.derivada_ia || c.estado === 'escalada';
      if (filtro === 'vistas') return !!c.vista_sin_responder;
      if (filtro === 'soporte') return c.categoria === 'soporte' || c.area === 'soporte';
      if (filtro === 'ventas') {
        const esVentas = c.categoria === 'ventas' || c.area === 'ventas';
        if (!esVentas) return false;
        const etapaVenta = obtenerVentaLocal(c)?.etapa || '';
        return etapaVenta !== 'Venta confirmada';
      }
      if (filtro === 'atencion') return c.categoria === 'atencion' || c.area === 'atencion';

      return true;
    });
  }, [busqueda, conversacionesPorVista, filtro]);

  const conversacionesOrdenadas = useMemo(() => {
    const prioridadValor = (conv) => {
      if (conv.estado === 'escalada' || conv.derivada_ia) return 0;
      if (conv.prioridad === 'critica') return 1;
      if (!conv.agente_id) return 2;
      return 3;
    };

    return [...conversacionesFiltradas].sort((a, b) => {
      const prioridadA = prioridadValor(a);
      const prioridadB = prioridadValor(b);

      if (prioridadA !== prioridadB) return prioridadA - prioridadB;

      const fechaA = new Date(a.updated_at || a.created_at || 0).getTime();
      const fechaB = new Date(b.updated_at || b.created_at || 0).getTime();
      return fechaB - fechaA;
    });
  }, [conversacionesFiltradas]);

  const stats = useMemo(
    () => ({
      activas: conversaciones.filter((c) => tieneAccesoArea(c.categoria || c.area) && c.estado !== 'cerrada').length,
      cerradas: conversaciones.filter((c) => tieneAccesoArea(c.categoria || c.area) && c.estado === 'cerrada').length,
      pendientes: conversaciones.filter(
        (c) => tieneAccesoArea(c.categoria || c.area) && !c.agente_id && c.estado !== 'cerrada'
      ).length,
      criticas: conversaciones.filter(
        (c) =>
          tieneAccesoArea(c.categoria || c.area) &&
          c.prioridad === 'critica' &&
          c.estado !== 'cerrada'
      ).length,
      derivadas: conversaciones.filter(
        (c) =>
          tieneAccesoArea(c.categoria || c.area) &&
          (c.derivada_ia || c.estado === 'escalada') &&
          c.estado !== 'cerrada'
      ).length,
      vistas: conversaciones.filter(
        (c) => tieneAccesoArea(c.categoria || c.area) && c.vista_sin_responder && c.estado !== 'cerrada'
      ).length,
    }),
    [conversaciones, selectedDemoUserId]
  );

  const categoryCounts = useMemo(() => {
    const activas = conversaciones.filter(
      (c) => ESTADOS_ACTIVOS.includes(c.estado) && tieneAccesoArea(c.categoria || c.area)
    );

    const esCategoria = (conv, categoria) =>
      conv.categoria === categoria || conv.area === categoria;

    return {
      todas: activas.length,
      soporte: activas.filter((c) => esCategoria(c, 'soporte')).length,
      ventas: activas.filter((c) => esCategoria(c, 'ventas')).length,
      atencion: activas.filter((c) => esCategoria(c, 'atencion')).length,
    };
  }, [conversaciones, selectedDemoUserId]);

  const areaMetrics = useMemo(() => {
    const activas = conversaciones.filter(
      (c) => ESTADOS_ACTIVOS.includes(c.estado) && tieneAccesoArea(c.categoria || c.area)
    );

    const ventas = activas.filter((c) => c.categoria === 'ventas' || c.area === 'ventas');
    const atencion = activas.filter((c) => c.categoria === 'atencion' || c.area === 'atencion');
    const soporte = activas.filter((c) => c.categoria === 'soporte' || c.area === 'soporte');

    const ventasCerradas = ventas.filter(
      (c) => (obtenerVentaLocal(c)?.etapa || '') === 'Venta confirmada'
    ).length;
    const ventasPendientes = ventas.filter((c) => {
      const etapa = obtenerVentaLocal(c)?.etapa || '';
      return etapa !== 'Venta confirmada' && etapa !== 'Venta perdida' && etapa !== 'Sin cobertura expansión';
    }).length;
    const ventasInstalacion = ventas.filter(
      (c) => false
    ).length;

    const atencionPago = atencion.filter(
      (c) => (obtenerAtencionLocal(c)?.estadoAdministrativo || '') === 'Esperando pago'
    ).length;
    const atencionResuelto = atencion.filter(
      (c) => (obtenerAtencionLocal(c)?.estadoAdministrativo || '') === 'Resuelto'
    ).length;
    const atencionPendiente = atencion.filter(
      (c) => (obtenerAtencionLocal(c)?.estadoAdministrativo || '') === 'Pendiente de revisión'
    ).length;

    const soporteVisita = soporte.filter(
      (c) => (obtenerSoporteLocal(c)?.visitaTecnica || '') === 'Sí'
    ).length;
    const soporteCurso = soporte.filter(
      (c) => (obtenerSoporteLocal(c)?.estadoTecnico || '') === 'Diagnóstico en curso'
    ).length;
    const soporteResuelto = soporte.filter(
      (c) => (obtenerSoporteLocal(c)?.estadoTecnico || '') === 'Resuelto'
    ).length;

    if (filtro === 'ventas' && areasHabilitadas.includes('ventas')) {
      return {
        titulo: 'Resumen Ventas',
        items: [
          { label: 'Pendientes', value: ventasPendientes },
          { label: 'Cerradas', value: ventasCerradas },
          { label: 'Instalación', value: ventasInstalacion },
        ],
      };
    }

    if (filtro === 'atencion' && areasHabilitadas.includes('atencion')) {
      return {
        titulo: 'Resumen Atención',
        items: [
          { label: 'Pendientes', value: atencionPendiente },
          { label: 'Esperando pago', value: atencionPago },
          { label: 'Resueltos', value: atencionResuelto },
        ],
      };
    }

    if (filtro === 'soporte' && areasHabilitadas.includes('soporte')) {
      return {
        titulo: 'Resumen Soporte',
        items: [
          { label: 'En curso', value: soporteCurso },
          { label: 'Con visita', value: soporteVisita },
          { label: 'Resueltos', value: soporteResuelto },
        ],
      };
    }

    const generalItems = [];
    if (areasHabilitadas.includes('ventas')) generalItems.push({ label: 'Ventas', value: ventas.length });
    if (areasHabilitadas.includes('atencion')) generalItems.push({ label: 'Atención', value: atencion.length });
    if (areasHabilitadas.includes('soporte')) generalItems.push({ label: 'Soporte', value: soporte.length });

    return {
      titulo: 'Resumen General',
      items: generalItems,
    };
  }, [conversaciones, ventasLocalData, atencionLocalData, soporteLocalData, filtro, selectedDemoUserId]);

  const emptyStateConfig = useMemo(() => {
    if (filtro === 'ventas') {
      return {
        icono: '💼',
        titulo: 'Bandeja de Ventas',
        texto: 'Seleccioná un lead para ver etapa comercial, próxima acción, cobertura y seguimiento.',
      };
    }

    if (filtro === 'soporte') {
      return {
        icono: '🛠️',
        titulo: 'Bandeja de Soporte',
        texto: 'Seleccioná un caso para ver diagnóstico, estado técnico, visita y acciones BCM.',
      };
    }

    if (filtro === 'atencion') {
      return {
        icono: '💳',
        titulo: 'Bandeja de Atención',
        texto: 'Seleccioná una conversación para ver deuda, factura, seguimiento y acciones administrativas.',
      };
    }

    return {
      icono: '📬',
      titulo: 'Inbox General',
      texto: 'Seleccioná una conversación para ver los mensajes.',
    };
  }, [filtro]);

  // ── FIX: categoriaActiva ahora cae al filtro activo si la conversación
  //         no tiene categoria/area en la BD ─────────────────────────────────
  const categoriaActiva = useMemo(() => {
    if (conversacionActiva) {
      return conversacionActiva.categoria || conversacionActiva.area || filtro || null;
    }
    if (filtro === 'ventas' || filtro === 'soporte' || filtro === 'atencion') {
      return filtro;
    }
    return null;
  }, [conversacionActiva, filtro]);

  const etiquetaCanalIA = (conv) => {
    return conv.ia_activa === false ? '👤 HUMANO' : '🤖 IA ACTIVA';
  };

  const obtenerBadgeEstado = (conv) => {
    const categoria = conv.categoria || conv.area;

    if (categoria === 'ventas') {
      return {
        texto: obtenerVentaLocal(conv)?.etapa || 'Lead nuevo',
        estilo: {
          background: '#EEF2FF',
          color: '#4338CA',
          border: '1px solid #C7D2FE',
        },
      };
    }

    if (categoria === 'atencion') {
      return {
        texto: obtenerAtencionLocal(conv)?.estadoAdministrativo || 'Pendiente de revisión',
        estilo: {
          background: '#ECFDF5',
          color: '#047857',
          border: '1px solid #A7F3D0',
        },
      };
    }

    if (categoria === 'soporte') {
      return {
        texto: obtenerSoporteLocal(conv)?.estadoTecnico || 'Pendiente de revisión',
        estilo: {
          background: '#FFF7ED',
          color: '#C2410C',
          border: '1px solid #FED7AA',
        },
      };
    }

    return null;
  };

  // Guard de autenticación
  if (!autenticado) return <Login onLogin={handleLogin} />;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  const ventaActual = obtenerVenta(conversacionActiva);
  const atencionActual = obtenerAtencion(conversacionActiva);
  const soporteActual = obtenerSoporte(conversacionActiva);

  return (
    <div className="app">
      <header className="header">
        <div className="header-top">
          <div className="logo-section">
            <div className="logo">WC</div>
            <div className="logo-text">
              <h1>WeConnect</h1>
              <p>
                Dashboard v2.0 · {currentUser.nivel.toUpperCase()} · {currentUser.nombre}
              </p>
              <p style={{ fontSize: 12, color: '#666' }}>
                Áreas: {areasHabilitadas.join(' · ')}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#666' }}>
              Agente logueado
            </label>
            <select
              value={agenteLogueadoId || ''}
              onChange={(e) => {
                const val = e.target.value || null;
                setAgenteLogueadoId(val);
                if (val) localStorage.setItem('weconnect_agente_id', val);
                else localStorage.removeItem('weconnect_agente_id');
              }}
              style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
            >
              <option value="">— Seleccioná tu usuario —</option>
              {agentes.filter((a) => a.activo !== false).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nombre}
                </option>
              ))}
            </select>
            {!agenteLogueadoId && (
              <span style={{ fontSize: 11, color: '#e53935', fontWeight: 600 }}>
                ⚠️ Seleccioná tu usuario antes de confirmar ventas
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#666' }}>
              Rol / permisos
            </label>
            <select
              value={selectedDemoUserId}
              onChange={(e) => setSelectedDemoUserId(e.target.value)}
              style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
            >
              {demoUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="stats-inline">
            <div className="stat-mini">
              <span>Activas:</span>
              <span className="stat-num gradient">{stats.activas}</span>
            </div>
            <div className="stat-mini pendientes">
              <span>Sin asignar:</span>
              <span className="stat-num warning">{stats.pendientes}</span>
            </div>
            <div className="stat-mini criticas">
              <span>Críticas:</span>
              <span className="stat-num danger">{stats.criticas}</span>
            </div>
            <div className="stat-mini criticas">
              <span>Derivadas IA:</span>
              <span className="stat-num danger">{stats.derivadas}</span>
            </div>
          </div>

          <div className="header-actions">
            <button
              className={`view-btn ${vistaActiva === 'activas' ? 'active' : ''}`}
              onClick={() => setVistaActiva('activas')}
            >
              📋 Activas ({stats.activas})
            </button>
            <button
              className={`view-btn ${vistaActiva === 'historial' ? 'active' : ''}`}
              onClick={() => setVistaActiva('historial')}
            >
              📚 Historial ({stats.cerradas})
            </button>

            {currentPermissions.viewMetrics && (
              <span className="badge">{agentes.filter((a) => a.online).length} agentes online</span>
            )}

            <button className="user-btn" onClick={handleLogout} title="Cerrar sesión">
              👤 {JSON.parse(localStorage.getItem('wc_agente') || '{}').nombre?.split(' ')[0] || 'Usuario'} · Salir
            </button>
          </div>
        </div>

        <div className="header-filters">
          <div className="quick-filters-inline">
            <button
              className={`qf-btn criticas ${filtro === 'criticas' ? 'active' : ''}`}
              onClick={() => setFiltro('criticas')}
            >
              🚨 Críticas <span className="qf-badge">{stats.criticas}</span>
            </button>
            <button
              className={`qf-btn sin-asignar ${filtro === 'pendientes' ? 'active' : ''}`}
              onClick={() => setFiltro('pendientes')}
            >
              Sin asignar <span className="qf-badge">{stats.pendientes}</span>
            </button>
            <button
              className={`qf-btn derivadas ${filtro === 'derivadas' ? 'active' : ''}`}
              onClick={() => setFiltro('derivadas')}
            >
              🤖 Derivadas IA <span className="qf-badge">{stats.derivadas}</span>
            </button>
            <button
              className={`qf-btn vistas ${filtro === 'vistas' ? 'active' : ''}`}
              onClick={() => setFiltro('vistas')}
            >
              Vistas sin resp. <span className="qf-badge">{stats.vistas}</span>
            </button>
          </div>

          <div className="search-inline">
            <input
              type="text"
              placeholder="Buscar por nombre o teléfono..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>

          <div className="filters-inline">
            <button
              className={`filter-pill ${filtro === 'todas' ? 'active' : ''}`}
              onClick={() => setFiltro('todas')}
            >
              Todas ({categoryCounts.todas})
            </button>

            {areasHabilitadas.includes('soporte') && (
              <button
                className={`filter-pill ${filtro === 'soporte' ? 'active' : ''}`}
                onClick={() => setFiltro('soporte')}
              >
                Soporte ({categoryCounts.soporte})
              </button>
            )}

            {areasHabilitadas.includes('ventas') && (
              <button
                className={`filter-pill ${filtro === 'ventas' ? 'active' : ''}`}
                onClick={() => setFiltro('ventas')}
              >
                Ventas ({categoryCounts.ventas})
              </button>
            )}

            {areasHabilitadas.includes('atencion') && (
              <button
                className={`filter-pill ${filtro === 'atencion' ? 'active' : ''}`}
                onClick={() => setFiltro('atencion')}
              >
                Atención ({categoryCounts.atencion})
              </button>
            )}
          </div>

          <div
            style={{
              marginTop: 12,
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <span style={{ fontWeight: 700, color: '#666' }}>{areaMetrics.titulo}:</span>
            {areaMetrics.items.map((item) => (
              <span
                key={item.label}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  borderRadius: 999,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {item.label}
                <span style={{ color: '#7c3aed' }}>{item.value}</span>
              </span>
            ))}
          </div>
        </div>
      </header>

      <div className="main">
        <div className="sidebar">
          <div className="sidebar-header">
            <span className="sidebar-title">{conversacionesOrdenadas.length} conversaciones</span>
            <div className="sidebar-sort">
              <select>
                <option>Más recientes</option>
                <option>Por prioridad</option>
              </select>
            </div>
          </div>

          <div className="conversations">
            {conversacionesOrdenadas.length === 0 ? (
              <div className="empty-state small">No hay conversaciones para mostrar</div>
            ) : (
              conversacionesOrdenadas.map((conv) => {
                const badgeEstado = obtenerBadgeEstado(conv);

                return (
                  <div
                    key={conv.id}
                    className={`conv-item ${conv.prioridad || ''} ${
                      conversacionActiva?.id === conv.id ? 'active' : ''
                    }`}
                    onClick={() => seleccionarConversacion(conv)}
                  >
                    <div className="conv-header">
                      <div className="conv-user">
                        <div className="avatar">{(conv.nombre_cliente || 'C')[0]}</div>
                        <div>
                          <div className="conv-name">{conv.nombre_cliente || 'Sin nombre'}</div>
                          <div className="conv-phone">{conv.telefono}</div>
                        </div>
                      </div>
                      <span className={`priority ${conv.prioridad || ''}`}>
                        {conv.derivada_ia || conv.estado === 'escalada'
                          ? '🔔 DERIVADA IA'
                          : conv.prioridad || 'normal'}
                      </span>
                    </div>

                    <div className="conv-meta-row">
                      <span className={`mode-badge ${conv.ia_activa === false ? 'human' : 'ia'}`}>
                        {etiquetaCanalIA(conv)}
                      </span>
                    </div>

                    {badgeEstado && (
                      <div className="conv-meta-row">
                        <span
                          className="op-state-pill"
                          style={badgeEstado.estilo}
                        >
                          {badgeEstado.texto}
                        </span>
                      </div>
                    )}

                    <div className="conv-preview">
                      {conv.ultimo_mensaje?.substring(0, 60) || 'Sin mensajes'}
                      {conv.ultimo_mensaje?.length > 60 ? '...' : ''}
                    </div>

                    <div className="conv-footer">
                      <span className="conv-area">{conv.categoria || 'sin categoría'}</span>
                      <span className="conv-time">
                        {conv.created_at ? new Date(conv.created_at).toLocaleDateString() : '-'}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="chat-area">
          {conversacionActiva ? (
            <>
              <div className="timeline">
                <div className="timeline-title">Timeline</div>

                <div className="timeline-event">
                  <div className="timeline-dot"></div>
                  <div className="timeline-time">
                    {conversacionActiva.created_at
                      ? new Date(conversacionActiva.created_at).toLocaleTimeString()
                      : '-'}
                  </div>
                  <div className="timeline-label">Cliente inicia</div>
                  <div className="timeline-detail">WhatsApp</div>
                </div>

                {conversacionActiva.derivada_ia && (
                  <div className="timeline-event">
                    <div
                      className="timeline-dot"
                      style={{ background: '#EAF3DE', borderColor: '#97C459' }}
                    ></div>
                    <div className="timeline-time">
                      {conversacionActiva.created_at
                        ? new Date(conversacionActiva.created_at).toLocaleTimeString()
                        : '-'}
                    </div>
                    <div className="timeline-label">🤖 IA atiende</div>
                    <div className="timeline-detail">
                      Categoría: {conversacionActiva.categoria || 'sin categoría'}
                    </div>
                  </div>
                )}

                {conversacionActiva.estado === 'cerrada' && conversacionActiva.cerrada_at && (
                  <div className="timeline-event">
                    <div
                      className="timeline-dot"
                      style={{ background: '#FCEBEB', borderColor: '#E24B4A' }}
                    ></div>
                    <div className="timeline-time">
                      {new Date(conversacionActiva.cerrada_at).toLocaleTimeString()}
                    </div>
                    <div className="timeline-label">✅ Cerrada</div>
                    <div className="timeline-detail">
                      Motivo: {conversacionActiva.motivo_cierre || '-'}
                    </div>
                    <div className="timeline-detail">
                      Cerrada por: {conversacionActiva.cerrada_por === 'ia' ? 'IA' : 'Agente'}
                    </div>
                  </div>
                )}
              </div>

              <div className="chat-main">
                <div className="chat-header">
                  <div className="chat-user-info">
                    <div className="avatar large">
                      {(conversacionActiva.nombre_cliente || 'C')[0]}
                    </div>
                    <div>
                      <h3>{conversacionActiva.nombre_cliente || 'Cliente'}</h3>
                      <p>{conversacionActiva.telefono}</p>
                      <div
                        className={`conversation-mode ${
                          conversacionActiva.ia_activa === false
                            ? 'conversation-mode--human'
                            : 'conversation-mode--ia'
                        }`}
                      >
                        {conversacionActiva.ia_activa === false ? '👤 Humano' : '🤖 IA activa'}
                      </div>
                    </div>
                  </div>

                  {conversacionActiva.estado !== 'cerrada' && (
                    <div className="chat-actions">
                      {currentPermissions.takeConversation && (
                        <button className="btn-action" onClick={tomarConversacion}>
                          ✋ Tomar
                        </button>
                      )}

                      {currentPermissions.transferConversation && (
                        <button className="btn-action" onClick={transferirConversacion}>
                          🔄 Transferir
                        </button>
                      )}

                      {currentPermissions.toggleIA && (
                        <button className="btn-action" onClick={toggleIA} disabled={cambiandoIA}>
                          {cambiandoIA
                            ? 'Procesando...'
                            : conversacionActiva.ia_activa === false
                            ? '🤖 Reactivar IA'
                            : '⛔ Anular IA'}
                        </button>
                      )}

                      {currentPermissions.closeConversation && (
                        <button className="btn-action danger" onClick={cerrarConversacion}>
                          ✓ Cerrar
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="chat-messages">
                  {mensajes.length === 0 ? (
                    <div className="empty-state">No hay mensajes para esta conversación</div>
                  ) : (
                    mensajes.map((msg, idx) => {
                      const inicial =
                        msg.rol === 'cliente'
                          ? (conversacionActiva.nombre_cliente || 'C')[0]
                          : msg.rol === 'ia'
                          ? '🤖'
                          : 'A';

                      return (
                        <div key={msg.id || idx} className={`message ${msg.rol || 'cliente'}`}>
                          <div className={`msg-avatar ${msg.rol || 'cliente'}`}>
                            {inicial}
                          </div>

                          <div className="msg-content">
                            <div className={`msg-bubble ${msg.rol || 'cliente'}`}>
                              {msg.contenido}
                            </div>
                            <div className="msg-time">
                              {msg.created_at
                                ? new Date(msg.created_at).toLocaleTimeString()
                                : '-'}
                              {msg.rol === 'ia'
                                ? ' • IA'
                                : msg.rol === 'agente'
                                ? ' • Agente'
                                : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {conversacionActiva.estado !== 'cerrada' && (
                  <div className="input-area">
                    <div className="input-box">
                      <input
                        type="text"
                        placeholder="Escribir mensaje... (Enter para enviar)"
                        value={nuevoMensaje}
                        onChange={(e) => setNuevoMensaje(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') enviarMensaje();
                        }}
                      />
                      <button className="btn-send" onClick={enviarMensaje} disabled={enviandoMensaje}>
                        {enviandoMensaje ? 'Enviando...' : 'Enviar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="info-sidebar">
                <div className="info-section">
                  <div className="info-title">
                    {categoriaActiva === 'ventas'
                      ? 'Contexto Comercial'
                      : categoriaActiva === 'soporte'
                      ? 'Contexto Técnico'
                      : categoriaActiva === 'atencion'
                      ? 'Contexto Administrativo'
                      : 'Datos del Cliente'}
                  </div>

                  <div
                    className={`conversation-context-banner ${
                      conversacionActiva.ia_activa === false
                        ? 'conversation-context-banner--human'
                        : 'conversation-context-banner--ia'
                    }`}
                  >
                    {conversacionActiva.ia_activa === false
                      ? '👤 Atención humana activa'
                      : '🤖 IA activa respondiendo'}
                  </div>

                  <div className="info-row">
                    <span className="info-label">Nombre:</span>
                    <span className="info-value">
                      {conversacionActiva.nombre_cliente || 'Sin nombre'}
                    </span>
                  </div>

                  <div className="info-row">
                    <span className="info-label">Teléfono:</span>
                    <span className="info-value">{conversacionActiva.telefono || '-'}</span>
                  </div>

                  {datosCliente ? (
                    <>
                      <div className="info-row">
                        <span className="info-label">Plan:</span>
                        <span className="info-value">{datosCliente.plan || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Estado:</span>
                        <span
                          className="info-value"
                          style={{
                            color: datosCliente.estado === 'Activo' ? '#28a745' : '#dc3545',
                          }}
                        >
                          {datosCliente.estado || '-'}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Deuda:</span>
                        <span className="info-value">${datosCliente.deuda ?? 0}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Saldo a favor:</span>
                        <span className="info-value">${datosCliente.saldo_a_favor ?? 0}</span>
                      </div>
                    </>
                  ) : (
                    <p className="loading-small">Cargando datos...</p>
                  )}
                </div>

                {categoriaActiva === 'ventas' && areasHabilitadas.includes('ventas') && ventaActual && (
                  <>
                    <div className="info-section">
                      <div className="info-title">Resumen comercial interno</div>

                      {conversacionActiva.derivada_ia && (
                        <div className="internal-badge">🤖 Lead derivado por IA</div>
                      )}

                      <div className="info-row">
                        <span className="info-label">Etapa:</span>
                        <span className="info-value">{ventaActual.etapa}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Cobertura:</span>
                        <span className="info-value">{ventaActual.cobertura}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Próxima acción:</span>
                        <span className="info-value">{ventaActual.proximaAccion}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Cliente:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.nombre_apellido || conversacionActiva.nombre_cliente || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">CUIL:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.cuil || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Mail:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.email || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Dirección:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.direccion || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Entre calles:</span>
                        <span className="info-value">
                          {formatearEntreCalles(
                            ventaActual.resumenInterno?.entre_calle_1,
                            ventaActual.resumenInterno?.entre_calle_2
                          ) || '-'}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Localidad:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.localidad || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Latitud:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.latitud || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Longitud:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.longitud || '-'}</span>
                      </div>
                      <div className="info-row" style={{ display: 'block' }}>
                        <span className="info-label">Mapa:</span>
                        <div className="info-value" style={{ textAlign: 'left', marginTop: 6 }}>
                          {ventaActual.resumenInterno?.mapa_url ? (
                            <a href={ventaActual.resumenInterno.mapa_url} target="_blank" rel="noreferrer" className="map-link">
                              Abrir ubicación
                            </a>
                          ) : (
                            '-'
                          )}
                        </div>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Teléfono:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.telefono || conversacionActiva.telefono || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Plan:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.plan || ventaActual.planSugerido || '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Precio:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.precio ? `$${ventaActual.resumenInterno.precio}` : '-'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Medio de pago:</span>
                        <span className="info-value">{ventaActual.resumenInterno?.medio_pago || '-'}</span>
                      </div>

                     <button
  className="action-btn primary"
  onClick={() => abrirModalVentas('confirmar')}
>
                        ✍️ Revisar resumen interno
                      </button>
                      <button className="action-btn success" onClick={() => confirmarVenta()}>
                        ✅ Confirmar venta
                      </button>
                    </div>

                    <div className="info-section">
                      <div className="info-title">Acciones del pipeline</div>
                      <button className="action-btn" onClick={() => accionRapidaVentas('contactar')}>
                        ☎️ Marcar contacto realizado
                      </button>
                      <button className="action-btn" onClick={() => accionRapidaVentas('calificar')}>
                        ✅ Calificar lead
                      </button>
                      <button className="action-btn" onClick={() => accionRapidaVentas('cobertura')}>
                        📍 Validar cobertura
                      </button>
                      <button className="action-btn" onClick={() => accionRapidaVentas('propuesta')}>
                        🧾 Enviar propuesta
                      </button>
                      <button className="action-btn" onClick={() => accionRapidaVentas('seguimiento')}>
                        📆 Programar seguimiento
                      </button>
                      <button className="action-btn" onClick={() => accionRapidaVentas('aceptar')}>
                        🤝 Registrar aceptación
                      </button>
                      <button className="action-btn" onClick={() => accionRapidaVentas('perdida')}>
                        ❌ Marcar venta perdida
                      </button>
                    </div>

                    <div className="info-section">
                      <div className="info-title">
                        Pipeline Comercial {guardandoEstado ? '· guardando...' : ''}
                      </div>

                      <div className="info-row">
                        <span className="info-label">Origen:</span>
                        <span className="info-value">{ventaActual.origen}</span>
                      </div>

                      <div className="info-row">
                        <span className="info-label">Prioridad:</span>
                        <span className="info-value">{ventaActual.prioridadComercial}</span>
                      </div>

                      <div className="info-row">
                        <span className="info-label">Última gestión:</span>
                        <span className="info-value">
                          {ventaActual.ultimaGestion
                            ? new Date(ventaActual.ultimaGestion).toLocaleString()
                            : '-'}
                        </span>
                      </div>

                      <div className="info-row" style={{ display: 'block' }}>
                        <span className="info-label">Observación interna:</span>
                        <textarea
                          style={{ width: '100%', marginTop: 8, minHeight: 80 }}
                          value={ventaActual.observacion || ''}
                          onChange={(e) =>
                            actualizarVentaLocal(conversacionActiva.id, {
                              resumenInterno: {
                                ...(ventaActual.resumenInterno || {}),
                                observaciones: e.target.value,
                              },
                            })
                          }
                          onBlur={async (e) => {
                            await guardarEstadoOperativo({
                              area: 'ventas',
                              campo: 'etapa_ventas',
                              valor: ventaActual.etapa,
                              proxima_accion: ventaActual.proximaAccion,
                              observacion: e.target.value || null,
                            });
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}{categoriaActiva === 'soporte' &&
                  areasHabilitadas.includes('soporte') &&
                  currentPermissions.viewTechnicalData &&
                  soporteActual && (
                    <>
                      <div className="info-section">
                        <div className="info-title">Acciones BCM {ejecutandoAccionBCM ? '· procesando...' : ''}</div>
                        <button className="action-btn" onClick={ejecutarDiagnostico} disabled={ejecutandoAccionBCM}>
                          🔍 Diagnóstico ONU
                        </button>
                        <button className="action-btn" onClick={reiniciarONU} disabled={ejecutandoAccionBCM || !diagnosticoBCM?.serial}>
                          🔄 Reiniciar ONU
                        </button>
                        {mensajeBCM && mensajeBCM.includes('Sin reconexión') && (
                          <button className="action-btn" onClick={verificarReinicioManual} style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)' }}>
                            🔍 Verificar ahora
                          </button>
                        )}
                        <button className="action-btn" onClick={desbloquearBCM} disabled={ejecutandoAccionBCM}>
                          🔓 Desbloquear en BCM
                        </button>
                        <button className="action-btn" onClick={confirmarSoporte} disabled={ejecutandoAccionBCM}
                          style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', fontWeight: 600 }}>
                          🎫 Generar ticket ISPCube
                        </button>
                        {mensajeBCM && (
                          <div style={{
                            marginTop: 10, padding: '8px 10px', borderRadius: 8, fontSize: 12, lineHeight: 1.5,
                            background: mensajeBCM.startsWith('❌') ? 'rgba(239,68,68,0.12)' : mensajeBCM.startsWith('⚠️') ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)',
                            color: mensajeBCM.startsWith('❌') ? '#fca5a5' : mensajeBCM.startsWith('⚠️') ? '#fde68a' : '#bbf7d0',
                            border: `1px solid ${mensajeBCM.startsWith('❌') ? 'rgba(239,68,68,0.25)' : mensajeBCM.startsWith('⚠️') ? 'rgba(245,158,11,0.25)' : 'rgba(16,185,129,0.25)'}`,
                          }}>
                            {mensajeBCM}
                          </div>
                        )}
                      </div>

                      {diagnosticoBCM && (
                        <div className="info-section">
                          <div className="info-title">Resultado diagnóstico</div>
                          <div className="info-row">
                            <span className="info-label">Estado ONU:</span>
                            <span className="info-value" style={{ color: diagnosticoBCM.estado_onu === 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                              {diagnosticoBCM.estado_onu === 0 ? '⛔ Sin potencia' : '✅ Online'}
                            </span>
                          </div>
                          {diagnosticoBCM.rx_power && (
                            <div className="info-row">
                              <span className="info-label">RX Power:</span>
                              <span className="info-value">{diagnosticoBCM.rx_power} dBm</span>
                            </div>
                          )}
                          {diagnosticoBCM.serial && (
                            <div className="info-row">
                              <span className="info-label">Serial ONU:</span>
                              <span className="info-value" style={{ fontSize: 11 }}>{diagnosticoBCM.serial}</span>
                            </div>
                          )}
                          <div className="info-row" style={{ display: 'block' }}>
                            <span className="info-label">Resumen:</span>
                            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
                              {diagnosticoBCM.diagnostico_resumen || '-'}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="info-section">
                        <div className="info-title">
                          Gestión Técnica {guardandoEstado ? '· guardando...' : ''}
                        </div>

                        <div className="info-row" style={{ display: 'block' }}>
                          <span className="info-label">Estado técnico:</span>
                          <select
                            style={{ width: '100%', marginTop: 8 }}
                            value={soporteActual.estadoTecnico}
                            onChange={async (e) => {
                              const estadoTecnico = e.target.value;
                              actualizarSoporteLocal(conversacionActiva.id, { estadoTecnico });
                              await guardarEstadoOperativo({
                                area: 'soporte', campo: 'estado_soporte', valor: estadoTecnico,
                                proxima_accion: soporteActual.proximaAccion,
                                visita_tecnica: soporteActual.visitaTecnica === 'Sí',
                                observacion: soporteActual.observacion || null,
                              });
                            }}
                          >
                            {ESTADOS_SOPORTE.map((estado) => (
                              <option key={estado} value={estado}>{estado}</option>
                            ))}
                          </select>
                        </div>

                        <div className="info-row" style={{ display: 'block' }}>
                          <span className="info-label">Diagnóstico:</span>
                          <input
                            type="text"
                            style={{ width: '100%', marginTop: 8 }}
                            value={soporteActual.diagnostico}
                            onChange={(e) => actualizarSoporteLocal(conversacionActiva.id, { diagnostico: e.target.value })}
                          />
                        </div>

                        <div className="info-row" style={{ display: 'block' }}>
                          <span className="info-label">Próxima acción:</span>
                          <input
                            type="text"
                            style={{ width: '100%', marginTop: 8 }}
                            value={soporteActual.proximaAccion}
                            onChange={(e) => actualizarSoporteLocal(conversacionActiva.id, { proximaAccion: e.target.value })}
                            onBlur={async (e) => {
                              await guardarEstadoOperativo({
                                area: 'soporte', campo: 'estado_soporte', valor: soporteActual.estadoTecnico,
                                proxima_accion: e.target.value,
                                visita_tecnica: soporteActual.visitaTecnica === 'Sí',
                                observacion: soporteActual.observacion || null,
                              });
                            }}
                          />
                        </div>

                        <div className="info-row" style={{ display: 'block' }}>
                          <span className="info-label">Visita técnica requerida:</span>
                          <select
                            style={{ width: '100%', marginTop: 8 }}
                            value={soporteActual.visitaTecnica}
                            onChange={async (e) => {
                              const visita = e.target.value;
                              actualizarSoporteLocal(conversacionActiva.id, { visitaTecnica: visita });
                              await guardarEstadoOperativo({
                                area: 'soporte', campo: 'estado_soporte', valor: soporteActual.estadoTecnico,
                                proxima_accion: soporteActual.proximaAccion,
                                visita_tecnica: visita === 'Sí',
                                observacion: soporteActual.observacion || null,
                              });
                            }}
                          >
                            <option value="No">No</option>
                            <option value="Sí">Sí</option>
                          </select>
                        </div>

                        <div className="info-row" style={{ display: 'block' }}>
                          <span className="info-label">Observación técnica:</span>
                          <textarea
                            style={{ width: '100%', marginTop: 8, minHeight: 80 }}
                            value={soporteActual.observacion}
                            onChange={(e) => actualizarSoporteLocal(conversacionActiva.id, { observacion: e.target.value })}
                            onBlur={async (e) => {
                              await guardarEstadoOperativo({
                                area: 'soporte', campo: 'estado_soporte', valor: soporteActual.estadoTecnico,
                                proxima_accion: soporteActual.proximaAccion,
                                visita_tecnica: soporteActual.visitaTecnica === 'Sí',
                                observacion: e.target.value,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                {categoriaActiva === 'atencion' &&
                  areasHabilitadas.includes('atencion') &&
                  currentPermissions.viewBilling &&
                  atencionActual && (
                    <>
                      <div className="info-section">
                        <div className="info-title">Acciones ISPCube</div>
                        <button
                          className="action-btn"
                          onClick={async () => {
                            try {
                              const convId = conversacionActiva.id;
                              const resp = await authFetch(`${API_URL}/api/dashboard/ispcube/factura/${convId}`);
                              const data = await resp.json();
                              if (!resp.ok) {
                                alert(`❌ ${data.error || 'Error buscando factura'}`);
                                return;
                              }
                              if (!data.factura) {
                                alert(`ℹ️ ${data.cliente}: No se encontraron facturas recientes`);
                                return;
                              }
                              const f = data.factura;
                              const venc = new Date(f.vencimiento).toLocaleDateString('es-AR');
                              const texto = `🧾 Factura ${f.numero} — ${f.periodo}\n💰 Total: $${f.total}\n📅 Vencimiento: ${venc}\n📄 ${f.url}`;
                              if (window.confirm(`${texto}\n\n¿Enviar al cliente por WhatsApp?`)) {
                                await authFetch(
                                  `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/mensaje`,
                                  {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ contenido: texto, agente_id: agenteActualId }),
                                  }
                                );
                                await cargarMensajes(conversacionActiva.id);
                              }
                            } catch (e) {
                              alert('❌ Error de conexión');
                            }
                          }}
                        >
                          📄 Ver factura
                        </button>
                        <button
                          className="action-btn"
                          onClick={async () => {
                            try {
                              const convId = conversacionActiva.id;
                              const resp = await authFetch(`${API_URL}/api/dashboard/ispcube/link-pago/${convId}`);
                              const data = await resp.json();
                              if (!resp.ok) {
                                alert(`❌ ${data.error || 'Error generando link'}`);
                                return;
                              }
                              if (!data.link) {
                                alert(`ℹ️ ${data.cliente}: ${data.mensaje || 'No se pudo generar el link'}`);
                                return;
                              }
                              const texto = `💳 Link de pago Mercado Pago:\n${data.link}\n\n💰 Deuda: $${data.balance}\n\n🏦 Portal SIRO: ${data.portal}`;
                              if (window.confirm(`${texto}\n\n¿Enviar al cliente por WhatsApp?`)) {
                                await authFetch(
                                  `${API_URL}/api/dashboard/conversacion/${conversacionActiva.id}/mensaje`,
                                  {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ contenido: texto, agente_id: agenteActualId }),
                                  }
                                );
                                await cargarMensajes(conversacionActiva.id);
                              }
                            } catch (e) {
                              alert('❌ Error de conexión');
                            }
                          }}
                        >
                          💳 Generar link MP
                        </button>
                        <button className="action-btn" onClick={() => alert('Función en desarrollo')}>
                          📋 Historial pagos
                        </button>
                      </div>

                      <div className="info-section">
                        <div className="info-title">Acciones Atención</div>
                        <button className="action-btn" onClick={() => accionRapidaAtencion('factura')}>
                          📄 Marcar factura enviada
                        </button>
                        <button className="action-btn" onClick={() => accionRapidaAtencion('pago')}>
                          💳 Marcar esperando pago
                        </button>
                        <button className="action-btn" onClick={() => accionRapidaAtencion('resuelto')}>
                          ✅ Marcar resuelto
                        </button>
                      </div>

                      <div className="info-section">
                        <div className="info-title">
                          Gestión Administrativa {guardandoEstado ? '· guardando...' : ''}
                        </div>

                        <div className="info-row" style={{ display: 'block' }}>
                          <span className="info-label">Estado administrativo:</span>
                          <select
                            style={{ width: '100%', marginTop: 8 }}
                            value={atencionActual.estadoAdministrativo}
                            onChange={async (e) => {
                              const estadoAdministrativo = e.target.value;
                              actualizarAtencionLocal(conversacionActiva.id, {
                                estadoAdministrativo,
                              });
                              await guardarEstadoOperativo({
                                area: 'atencion',
                                campo: 'estado_atencion',
                                valor: estadoAdministrativo,
                                proxima_accion: atencionActual.proximaAccion,
                                observacion: atencionActual.observacion || null,
                              });
                            }}
                          >
                            {ESTADOS_ATENCION.map((estado) => (
                              <option key={estado} value={estado}>
                                {estado}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="info-row">
                          <span className="info-label">Última factura:</span>
                          <span className="info-value">Abril 2026</span>
                        </div>

                        <div className="info-row">
                          <span className="info-label">Vencimiento:</span>
                          <span className="info-value">18/04</span>
                        </div>

                        <div className="info-row">
                          <span className="info-label">Portal:</span>
                          <span className="info-value">SIRO / Mercado Pago</span>
                        </div>

                        <div className="info-row" style={{ display: 'block' }}>
                          <span className="info-label">Próxima acción:</span>
                          <input
                            type="text"
                            style={{ width: '100%', marginTop: 8 }}
                            value={atencionActual.proximaAccion}
                            onChange={(e) =>
                              actualizarAtencionLocal(conversacionActiva.id, {
                                proximaAccion: e.target.value,
                              })
                            }
                            onBlur={async (e) => {
                              await guardarEstadoOperativo({
                                area: 'atencion',
                                campo: 'estado_atencion',
                                valor: atencionActual.estadoAdministrativo,
                                proxima_accion: e.target.value,
                                observacion: atencionActual.observacion || null,
                              });
                            }}
                          />
                        </div>

                        <div className="info-row" style={{ display: 'block' }}>
                          <span className="info-label">Observación administrativa:</span>
                          <textarea
                            style={{ width: '100%', marginTop: 8, minHeight: 80 }}
                            value={atencionActual.observacion}
                            onChange={(e) =>
                              actualizarAtencionLocal(conversacionActiva.id, {
                                observacion: e.target.value,
                              })
                            }
                            onBlur={async (e) => {
                              await guardarEstadoOperativo({
                                area: 'atencion',
                                campo: 'estado_atencion',
                                valor: atencionActual.estadoAdministrativo,
                                proxima_accion: atencionActual.proximaAccion,
                                observacion: e.target.value,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                {conversacionActiva.estado !== 'cerrada' && (
                  <div className="info-section">
                    <div className="info-title">Gestión de conversación</div>

                    {currentPermissions.toggleIA && (
                      <button className="action-btn" onClick={toggleIA} disabled={cambiandoIA}>
                        {cambiandoIA
                          ? 'Procesando...'
                          : conversacionActiva.ia_activa === false
                          ? '🤖 Reactivar IA'
                          : '⛔ Anular IA'}
                      </button>
                    )}

                    {currentPermissions.transferConversation && (
                      <button className="action-btn" onClick={transferirConversacion}>
                        🔄 Transferir a otra área
                      </button>
                    )}

                    {currentPermissions.closeConversation && (
                      <button className="action-btn" onClick={cerrarConversacion}>
                        ✅ Cerrar conversación
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="chat-empty">
              <p className="chat-empty-icon">{emptyStateConfig.icono}</p>
              <h3 className="chat-empty-title">{emptyStateConfig.titulo}</h3>
              <p className="chat-empty-text">{emptyStateConfig.texto}</p>
            </div>
          )}
        </div>
      </div>
{salesModal.open && (
        <div className="modal-overlay" onClick={cerrarModalVentas}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{{
                  contactar: 'Marcar contacto realizado',
                  calificar: 'Calificar lead',
                  cobertura: 'Validar cobertura',
                  propuesta: 'Enviar propuesta',
                  seguimiento: 'Programar seguimiento',
                  aceptar: 'Registrar aceptación',
                  perdida: 'Marcar venta perdida',
                  confirmar: 'Revisar resumen interno',
                }[salesModal.type]}</h3>
                <p>Gestión interna comercial. No visible para el cliente.</p>
              </div>
              <button className="modal-close" onClick={cerrarModalVentas}>✕</button>
            </div>

            <div className="modal-body">
              {salesModal.type === 'contactar' && (
                <>
                  <div className="modal-field">
                    <label>Próxima acción</label>
                    <input
                      value={salesModalForm.proxima_accion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, proxima_accion: e.target.value }))}
                      placeholder="Ej: Continuar calificación comercial"
                    />
                  </div>
                  <div className="modal-field">
                    <label>Observación</label>
                    <textarea
                      value={salesModalForm.observacion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, observacion: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {salesModal.type === 'calificar' && (
                <>
                  <div className="modal-field">
                    <label>Resultado</label>
                    <select
                      value={salesModalForm.resultado_calificacion || 'calificado'}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, resultado_calificacion: e.target.value }))}
                    >
                      <option value="calificado">Calificado</option>
                      <option value="descartar">Descartar</option>
                    </select>
                  </div>
                  <div className="modal-field">
                    <label>Próxima acción</label>
                    <input
                      value={salesModalForm.proxima_accion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, proxima_accion: e.target.value }))}
                      placeholder="Ej: Validar cobertura"
                    />
                  </div>
                  <div className="modal-field">
                    <label>Observación</label>
                    <textarea
                      value={salesModalForm.observacion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, observacion: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {salesModal.type === 'cobertura' && (
                <>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label>Dirección</label>
                      <input
                        value={salesModalForm.direccion || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, direccion: e.target.value }))}
                      />
                    </div>
                    <div className="modal-field">
                      <label>Localidad</label>
                      <input
                        value={salesModalForm.localidad || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, localidad: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label>Entre calle 1 (opcional)</label>
                      <input
                        value={salesModalForm.entre_calle_1 || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, entre_calle_1: e.target.value }))}
                      />
                    </div>
                    <div className="modal-field">
                      <label>Entre calle 2 (opcional)</label>
                      <input
                        value={salesModalForm.entre_calle_2 || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, entre_calle_2: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-field">
                    <label>Resultado cobertura</label>
                    <select
                      value={salesModalForm.cobertura_resultado || 'cobertura_ok'}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, cobertura_resultado: e.target.value }))}
                    >
                      <option value="cobertura_ok">Cobertura OK</option>
                      <option value="sin_cobertura">Sin cobertura</option>
                      <option value="sin_cobertura_expansion">Sin cobertura / guardar para expansión</option>
                    </select>
                  </div>
                  <div className="modal-field">
                    <label>Observación</label>
                    <textarea
                      value={salesModalForm.observacion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, observacion: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {salesModal.type === 'propuesta' && (
                <>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label>Plan</label>
                      <input
                        value={salesModalForm.plan || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, plan: e.target.value }))}
                      />
                    </div>
                    <div className="modal-field">
                      <label>Precio</label>
                      <input
                        value={salesModalForm.precio || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, precio: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-field">
                    <label>Medio de pago</label>
                    <input
                      value={salesModalForm.medio_pago || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, medio_pago: e.target.value }))}
                    />
                  </div>
                  <div className="modal-field">
                    <label>Próxima acción</label>
                    <input
                      value={salesModalForm.proxima_accion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, proxima_accion: e.target.value }))}
                    />
                  </div>
                  <div className="modal-field">
                    <label>Observación</label>
                    <textarea
                      value={salesModalForm.observacion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, observacion: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {salesModal.type === 'seguimiento' && (
                <>
                  <div className="modal-field">
                    <label>Próxima acción</label>
                    <input
                      value={salesModalForm.proxima_accion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, proxima_accion: e.target.value }))}
                    />
                  </div>
                  <div className="modal-field">
                    <label>Fecha de seguimiento</label>
                    <input
                      type="datetime-local"
                      value={salesModalForm.fecha_seguimiento || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, fecha_seguimiento: e.target.value }))}
                    />
                  </div>
                  <div className="modal-field">
                    <label>Observación</label>
                    <textarea
                      value={salesModalForm.observacion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, observacion: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {salesModal.type === 'aceptar' && (
                <>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label>Plan final</label>
                      <input
                        value={salesModalForm.plan || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, plan: e.target.value }))}
                      />
                    </div>
                    <div className="modal-field">
                      <label>Precio final</label>
                      <input
                        value={salesModalForm.precio || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, precio: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-field">
                    <label>Observación</label>
                    <textarea
                      value={salesModalForm.observacion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, observacion: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {salesModal.type === 'perdida' && (
                <>
                  <div className="modal-field">
                    <label>Motivo de pérdida</label>
                    <select
                      value={salesModalForm.motivo_perdida || 'No responde'}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, motivo_perdida: e.target.value }))}
                    >
                      <option>Precio</option>
                      <option>Sin cobertura</option>
                      <option>Competencia</option>
                      <option>No responde</option>
                      <option>Postergó</option>
                      <option>Otro</option>
                    </select>
                  </div>
                  <div className="modal-field">
                    <label>Observación</label>
                    <textarea
                      value={salesModalForm.observacion || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, observacion: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {salesModal.type === 'confirmar' && (
                <>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label>Nombre y apellido *</label>
                      <input
                        value={salesModalForm.nombre_apellido || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, nombre_apellido: e.target.value }))}
                      />
                    </div>
                    <div className="modal-field">
                      <label>CUIL</label>
                      <input
                        value={salesModalForm.cuil || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, cuil: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label>Mail</label>
                      <input
                        value={salesModalForm.email || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, email: e.target.value }))}
                      />
                    </div>
                    <div className="modal-field">
                      <label>Teléfono *</label>
                      <input
                        value={salesModalForm.telefono || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, telefono: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label>Dirección *</label>
                      <input
                        value={salesModalForm.direccion || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, direccion: e.target.value }))}
                      />
                    </div>
                    <div className="modal-field">
                      <label>Localidad *</label>
                      <input
                        value={salesModalForm.localidad || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, localidad: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label>Entre calle 1 (opcional)</label>
                      <input
                        value={salesModalForm.entre_calle_1 || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, entre_calle_1: e.target.value }))}
                      />
                    </div>
                    <div className="modal-field">
                      <label>Entre calle 2 (opcional)</label>
                      <input
                        value={salesModalForm.entre_calle_2 || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, entre_calle_2: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-grid">
                    <div className="modal-field">
                      <label>Plan</label>
                      <input
                        value={salesModalForm.plan || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, plan: e.target.value }))}
                      />
                    </div>
                    <div className="modal-field">
                      <label>Precio</label>
                      <input
                        value={salesModalForm.precio || ''}
                        onChange={(e) => setSalesModalForm((prev) => ({ ...prev, precio: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-field">
                    <label>Medio de pago</label>
                    <input
                      value={salesModalForm.medio_pago || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, medio_pago: e.target.value }))}
                    />
                  </div>
                  <div className="modal-field">
                    <label>Observación interna</label>
                    <textarea
                      value={salesModalForm.observaciones || ''}
                      onChange={(e) => setSalesModalForm((prev) => ({ ...prev, observaciones: e.target.value }))}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-action" onClick={cerrarModalVentas}>Cancelar</button>
              <button className="btn-send" onClick={guardarAccionVentasModal} disabled={submittingSalesModal}>
                {submittingSalesModal ? 'Guardando...' : salesModal.type === 'confirmar' ? 'Guardar resumen' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
